import { Mat4 } from "@base/math/Mat4";
import { Degree, MathUtil } from "@base/math/MathUtil";
import { Plane } from "@base/math/Plane";
import { Ray3 } from "@base/math/Ray3";
import { Vec2 } from "@base/math/Vec2";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { eqc, gtc, lec, ltc } from "@base/util/Comma";
import { StringUtil } from "@base/util/StringUtil";
import { getKeys, mapCount } from "@base/util/TypeUtil";
import { Util } from "@base/util/Util";
import { RopeHelper } from "@biz/articles/RopeHelper";
import { SYSTEM_PACKAGING } from "@biz/Definitions";
import { Access } from "@biz/definitions/AccessDefinitions";
import { AdditionalArticleInfos, AdditionalArticleInfosOption, getPolyOrPylonArticleId, IArticleComputedInfo, IRuleArticle, OfferListType, RuleArticleMap } from "@biz/definitions/ArticleDefinitions";
import { FL } from "@biz/definitions/FL";
import { IMinMax } from "@biz/definitions/MinMax";
import { bedienSetOne, IPointMountInfo, seilStoppWinchArticleId } from "@biz/definitions/MountDefinitons";
import { Product } from "@biz/definitions/Product";
import { Pvi } from "@biz/definitions/ProductVersion";
import { SST } from "@biz/definitions/SST";
import { ClothType, ISunSailData, ISunSailSide, motors, PL_AXLE_GROUP_NAME, PoleId, PoleIndex, PoleMountType, PoleType, SunSailType } from "@biz/definitions/SunSailDefinitions";
import { getPointName, getSideName, getSideNameSmall } from "@biz/definitions/SunSailDefinitions.util";
import { CalcProductLogic } from "@biz/items/CalcProductLogic";
import { CalcSailLogic } from "@biz/items/sunSail/CalcSailLogic";
import { IndirectPylonLogic } from "@biz/items/sunSail/IndirectPylonLogic";
import { AmplifierLogic, IPoleAmplifierInfo, poleAmplifierArticles } from "@biz/items/sunSail/logic/AmplifierLogic";
import { AxleLogic } from "@biz/items/sunSail/logic/AxleLogic";
import { FlexLogic } from "@biz/items/sunSail/logic/FlexLogic";
import { HeightChangeLogic } from "@biz/items/sunSail/logic/HeightChangeLogic";
import { LogicS } from "@biz/items/sunSail/logic/LogicS";
import { PoleChargeOptimizer } from "@biz/items/sunSail/logic/PoleChargeOptimizer";
import { PoleHelper } from "@biz/items/sunSail/logic/PoleHelper";
import { PoleLogic } from "@biz/items/sunSail/logic/PoleLogic";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { SandyLogic } from "@biz/items/sunSail/logic/SandyLogic";
import { ONE_AXLE_WALL_PLATES, SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";
import { SunSailMath } from "@biz/items/sunSail/logic/SunSailMath";
import { SunSailTopology } from "@biz/items/sunSail/logic/SunSailTopology";
import { Sail2dPoly } from "@biz/items/sunSail/Sail2dPoly";
import { ILocaContext, LocaContext } from "@biz/language/LocaContext";
import { AdditionalArticleInfo } from "@biz/logic/AdditionalArticleInfo";
import { ClothData } from "@biz/logic/ClothData";
import { Measure } from "@biz/logic/Measure";
import { MotorLogic } from "@biz/logic/MotorLogic";
import { RuleArticleHelper } from "@biz/logic/RuleArticleHelper";
import { FieldErrorMap } from "@biz/models/FieldErrorMap";
import { ArticleGroup, IPartListProperties, IShoppingArticleOptions, IShoppingListContext, PartListPriority, QuickOverlayPriority, ShoppingCartPriority } from "@biz/models/ShoppingListDefinitions";
import { BeosVarianten } from "@biz/orders/BeosVarianten";
import { PD } from "@biz/PD";
import { ICalcContext, ICalculatedProductResult, ICalculatedShoppingCart, IProjectCheckError, IProjectInfoValue, ProjectCheckErrorPosition, ProjectCheckErrorType } from "@biz/projectCalculation/ProjectDefinitions";

export function getUserManualForSunSail(sunSailType: SunSailType, language: string, isOrig?: boolean): string {
  const lang = `Sprache: <strong><mark>${Util.capitalizeFirstLetter(language)}</mark></strong>`
  const orig = isOrig ? "Original " : "";
  switch (sunSailType) {
    case SunSailType.SolidayA:
      return orig + 'Bedienungsanleitung "Soliday MA"  ' + lang;
    case SunSailType.SolidayOne:
      return orig + 'Bedienungsanleitung "Soliday-ONE"  ' + lang;
    case SunSailType.SolidayFlex:
      return orig + 'Bedienungsanleitung "Soliday-FLEX"  ' + lang;
    case SunSailType.SolidayM:
      return orig + 'Bedienungsanleitung "Soliday M"  ' + lang;
    case SunSailType.SolidayC:
      return orig + 'Bedienungsanleitung "Soliday C / CS / CS Twin  ' + lang;
    case SunSailType.SolidayCS:
      return orig + 'Bedienungsanleitung "Soliday C / CS / CS Twin  ' + lang;
    case SunSailType.SolidayTwin:
      return orig + 'Bedienungsanleitung "Soliday C / CS / CS Twin  ' + lang;
  }
  return orig + "Bedienungsnanleitung";
}

export class CalcSail3dLogic {
  static addSystemSailArticles(slc: ICalcContext, ssd: ISunSailData): void {
    switch (ssd.sunSailType) {
      case SunSailType.SolidayA: CalcSail3dLogic.addSailAArticles(slc, ssd); break;
      case SunSailType.SolidayM: CalcSail3dLogic.addSailMArticles(slc, ssd); break;
      case SunSailType.SolidayC: CalcSail3dLogic.addSailCArticles(slc, ssd); break;
      case SunSailType.SolidayX: CalcSail3dLogic.addSailXArticles(slc, ssd); break;
      case SunSailType.SolidayOne: CalcSail3dLogic.addSailOneArticles(slc, ssd); break;
      case SunSailType.SolidayFlex: CalcSail3dLogic.addSailFlexArticles(slc, ssd); break;
      case SunSailType.SolidayCS: CalcSail3dLogic.addSailCSArticles(slc, ssd); break;
      case SunSailType.SolidayXS: CalcSail3dLogic.addSailXSArticles(slc, ssd); break;
      case SunSailType.SolidayTwin: CalcSail3dLogic.addSailTwinArticles(slc, ssd); break;
      case SunSailType.SandyPoly: case SunSailType.SandyRect: CalcSail3dLogic.addSailSandyArticles(slc, ssd); break;
    }
  }

  static addSystemArticles(slc: ICalcContext, ssd: ISunSailData): void {
    const sunSailType = ssd.sunSailType;
    switch (sunSailType) {
      case SunSailType.SolidayA: CalcSail3dLogic.addSystemAArticles(slc, ssd); break;
      case SunSailType.SolidayM: CalcSail3dLogic.addSystemMArticles(slc, ssd); break;
      case SunSailType.SolidayC: CalcSail3dLogic.addSystemCArticles(slc, ssd); break;
      case SunSailType.SolidayX: CalcSail3dLogic.addSystemXArticles(slc, ssd); break;
      case SunSailType.SolidayOne: CalcSail3dLogic.addSystemOneArticles(slc, ssd); break;
      case SunSailType.SolidayFlex: CalcSail3dLogic.addSystemFlexArticles(slc, ssd); break;
      case SunSailType.SolidayCS: CalcSail3dLogic.addSystemCSArticles(slc, ssd); break;
      case SunSailType.SolidayXS: CalcSail3dLogic.addSystemXSArticles(slc, ssd); break;
      case SunSailType.SolidayTwin: CalcSail3dLogic.addSystemTwinArticles(slc, ssd); break;
      case SunSailType.SandyPoly: case SunSailType.SandyRect: CalcSail3dLogic.addSystemSandyArticles(slc, ssd); break;
    }
  }

  static calc3d(ssd: ISunSailData, slc: ICalcContext): ICalculatedProductResult {
    const cloth = SunSailLogic.getCloth(ssd);
    CalcProductLogic.checkValidClothData(slc, PD.productToPD(ssd), FL.fromProduct(ssd), cloth, ssd.colorId);

    CalcSailLogic.addArticles(slc, ssd);

    CalcSail3dLogic.doChecks3d(slc, slc.ruleArticles, ssd);
    CalcSail3dLogic.checkNoHardwareForHardwareOrder(slc, ssd);

    CalcSail3dLogic.doInfos3d(slc, ssd);

    CalcProductLogic.finalizeCalcContext(slc, { incomplete: !ssd, defaultOfferList: "H", count: ssd.count });

    return {
      id: ssd.id,
      pd: PD.productToPD(ssd),
      infos: slc.infos,
      errors: slc.errors,
      errorMap: FieldErrorMap.errorMapFromErrors(slc.errors || []),
      articles: slc.ruleArticles,
      // modified: sunSail,
      product: ssd
      // modifiedSunSail: sunSail,
    };
  }

  static checkSoWiRemoteGlobal(products: Record<string, ICalculatedProductResult>, shoppingCart: ICalculatedShoppingCart, errors: IProjectCheckError[], lc: ILocaContext, hasSoWiCentronic: boolean, hasSoWiCentronicPlus: boolean): void {
    const articles: IRuleArticle[] = [];
    for (const ak of Object.keys(shoppingCart.articles)) {
      const article = shoppingCart.articles[ak];
      if (article.count > 0 && !article.customArticle)
        articles.push(article);
    }

    for (const k of Object.keys(products)) {
      const product = products[k];
      for (const ak of Object.keys(product.articles)) {
        const article = product.articles[ak];
        if (article.count > 0 && !article.customArticle)
          articles.push(article);
      }
    }

    for (const a of articles) {
      if (!hasSoWiCentronic) {
        if (a.articleNumber === "71003A005") {
          CalcProductLogic.addError(errors, ProjectCheckErrorType.Hint, "project", lc.t("Rule_SoWi_Remote"))
          return;
        }
      }
      if (!hasSoWiCentronicPlus) {
        if (MotorLogic.getSunWindSensors("centronic+").find((s) => s.articleNumber === a.articleNumber)) {
          CalcProductLogic.addError(errors, ProjectCheckErrorType.Hint, "project", lc.t("Rule_SoWi_RemotePlus"))
          return;
        }
      }
    }
  }

  static checkSnapWinchGlobal(products: Record<string, ICalculatedProductResult>, errors: IProjectCheckError[], lc: ILocaContext): void {
    let snapWinch = false;
    let fallStopp = false;
    for (const k of Object.keys(products)) {
      const product = products[k];
      const pd = product.pd;
      if (PD.isSail(pd)) {
        // if (SST.isIn(sunSailType, SST.XXs)) {
        //   snapWinch = true; // enthalten in X/XS
        //   fallStopp = true;
        // } else
        {
          for (const ak of Object.keys(product.articles)) {
            const a = product.articles[ak];
            if (SunSailLogic.isSnapWinch(a.articleNumber) && a.count > 0) {
              snapWinch = true;
            }
            if (SunSailLogic.isFallStopp(a.articleNumber) && a.count > 0) {
              fallStopp = true;
            }
          }
        }
      }
    }
    if (!snapWinch && fallStopp)
      CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, "project", lc.t("Rule_One_SnapWinch_Required_Per_Project"))
  }

  // static getCloth(sunSail: ISunSailData): IClothData | undefined {
  //   return sunSail.cloth || ClothDataLogic.getDefaultClothData(PD.productToPD(sunSail), FL.fromProduct(sunSail));
  // }

  static doChecks3d(slc: ICalcContext, readOnlyArticles: RuleArticleMap, ssd: ISunSailData): void {
    for (const poleId of Poles.getPoleIds(ssd)) {
      CalcSail3dLogic.checkPoint(slc, readOnlyArticles, ssd, poleId);
    }

    CalcSail3dLogic.checkNeedsCoilUp(slc, ssd);
    CalcSailLogic.checkOperationDefelectionPoint(slc, ssd);
    CalcSailLogic.checkSandy(slc, ssd);
    CalcSail3dLogic.checkAxle(slc, ssd);
    CalcSailLogic.checkAngles(slc, ssd.sunSailType, Poles.getCount(ssd), SunSailLogic.calcNetPositionGroups(ssd, {}));
    CalcSailLogic.checkSideRatio(ssd, slc.errors, slc.lc);

    CalcSailLogic.checkCSPolePositions(slc, ssd);
    CalcSailLogic.checkPylons(ssd, slc.errors, slc.lc.t);
    CalcSail3dLogic.checkSolidayC(slc, ssd);

    CalcSailLogic.checkAlignDistOut(slc, ssd);
    CalcSailLogic.checkMaxNetSideLength(slc, ssd);
    CalcSailLogic.checkMinAngles(slc, ssd);
    CalcSailLogic.checkMaxMountSideLength(slc, ssd);
    CalcSail3dLogic.checkConvexNet(slc, ssd);

    CalcSail3dLogic.checkSymmetric(slc, ssd);

    CalcSail3dLogic.checkTwistingSetRequired(slc, ssd);
    CalcSailLogic.checkMaxSunSailArea(ssd, slc);

    CalcSail3dLogic.checkSandyOutsidePolygon(slc, ssd);
    CalcSail3dLogic.checkWindsensor(slc, ssd);
  }

  static checkWindsensor(slc: ICalcContext, ssd: ISunSailData) {
    if (MotorLogic.hasMotor(ssd.sunSailType)) {
      const count = RuleArticleHelper.countArticles(slc.ruleArticles, MotorLogic.getSunWindSensors("all").map((s) => s.articleNumber), undefined).totalCount;
      // console.log("Count ", count);
      if (count === 0)
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Hint, "product", slc.lc.t("Error_MotorWithoutRemote"));// https://plaspack.atlassian.net/browse/APP-1512
    }
  }

  static checkSandyOutsidePolygon(slc: ICalcContext, ssd: ISunSailData): void {
    const polys = Sail2dPoly.calcSvgPolys(ssd);
    for (let iPole = 0; iPole < Poles.getCount(ssd, "center"); iPole++) {
      const pos = ssd.centerPoles![iPole].position;
      if (!Sail2dPoly.isPointInPolys(polys, Vec2.fromXZ(pos))) {
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, getPointName(Poles.toPoleId(iPole, "center")), slc.lc.t("Error_CenterPole_Outside_Poly"))
      }
    }
  }

  static checkSymmetric(slc: ICalcContext, ssd: ISunSailData): void {
    const sunSailType = ssd.sunSailType;
    if (SST.is(sunSailType, SST.CsTwin)) {
      CalcSail3dLogic.checkSymmetricCS(slc.errors, ssd, PoleIndex.C, PoleIndex.D, slc.lc);
      CalcSail3dLogic.checkSymmetricCS(slc.errors, ssd, PoleIndex.E, PoleIndex.F, slc.lc);
    }

    if (SST.is(sunSailType, SST.Xs) && Poles.getCount(ssd) === 4) {
      CalcSail3dLogic.checkSymmetricXS(slc.errors, ssd, slc.lc);
    }
  }

  static checkTwistingSetRequired(slc: ICalcContext, ssd: ISunSailData): void {
    const sunSailType = ssd.sunSailType;
    if (AxleLogic.hasAxle(sunSailType)) {
      if (!ssd.twistingSet && SunSailLogic.isTwistingSetRequired(ssd)) {
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "product", slc.lc.t("Rule_TwistingSet_Required"));
      }
    }
  }

  static addAllPointArticles(slc: IShoppingListContext, ssd: ISunSailData): void {
    const mountPositions = SunSailLogic.calcMountPositions(ssd, 0, undefined);
    const poleIds = Poles.getPoleIds(ssd);
    for (const poleId of poleIds) {
      CalcSail3dLogic.addPointArticles(slc, ssd, poleId, mountPositions);
    }

    const sunSailType = ssd.sunSailType;
    if (SST.is(sunSailType, SST.CoSF, SST.Flex)) {
      for (const poleId of poleIds) {
        CalcSail3dLogic.addAdditionalPoleArticles(slc, ssd, poleId);
      }
    }
  }

  static checkNeedsCoilUp(slc: ICalcContext, ssd: ISunSailData): void {
    const sunSailType = ssd.sunSailType;
    if (SunSailLogic.needsSailCoilUp(sunSailType)) {
      const coilUpCount = SunSailLogic.calcSailCoilUpCount(ssd);
      if (coilUpCount !== 1)
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "product", slc.lc.t("Rule_OneCoilUp"), { params: coilUpCount})
    }
  }

  private static checkNoHardwareForHardwareOrder(slc: ICalcContext, sunSail: ISunSailData): void {
    if (!CalcProductLogic.hasMainArticles(slc, "H") && CalcProductLogic.hasMainArticles(slc, "S") && Product.isAllInOne(PD.productToPD(sunSail), Product.calcSourceConfigurator(sunSail)).possible && sunSail.orderType !== "HS") {
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "net", slc.lc.t("Rule_NoHardware_AllInOne"));
    }
  }

  static addStatikArticle(slc: IShoppingListContext, sunSail: ISunSailData): void {
    if (!SST.isRaff(sunSail.sunSailType)) { // STATIK
      CalcProductLogic.addRuleArticle(slc, "99STATIK", { count: 0, articleType: "optional" }); // https://plaspack.atlassian.net/browse/SA-44
    }
  }

  static checkConvexNet(slc: ICalcContext, sunSail: ISunSailData): void {
    // ***** Convex 816
    const sunSailType = sunSail.sunSailType;
    if (!SST.is(sunSailType, SST.CsXsTwin, SST.Sandy)) {
      const netPositionGroups = SunSailLogic.calcNetPositionGroups(sunSail, {});
      if (!SunSailLogic.isConvex(netPositionGroups[0]))
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "product", slc.lc.t("Rule_PolyConvex"));
    }
  }
  static addIndirectPylonArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    // ***** Pylon Indirect 846
    const indirectPylons = IndirectPylonLogic.getIndirectPylons(sunSail);
    for (const indirectPylon of indirectPylons) {
      const axlePylonName = getPointName(indirectPylon.axlePylon);
      const infoName = "indirectPylon_" + axlePylonName;
      CalcProductLogic.addRuleArticle(slc, "00E13000107", { count: 1, mul: 6, articleGroup: "indirectPylon", idCategory: infoName }); // Pylonseil 4mm
      CalcProductLogic.addRuleArticle(slc, "00E13001151", { count: 1, articleGroup: "indirectPylon", idCategory: infoName }); // Schwerlastrolle Mitte

      {
        const poleIndex = indirectPylon.oppositeAxlePylon;
        const mount = IndirectPylonLogic.calcAxlePylonRopeMountType(sunSail, poleIndex)
        const poleLength = SunSailLogic.getPoleLength(sunSail, poleIndex);
        if (mount)
          CalcSail3dLogic.addSecondaryArticles(slc, sunSail, poleIndex, mount, { articleGroup: "indirectPylon", computed: { polePosition: poleLength - SunSailLogic.calcTopMountY(sunSail, poleIndex) + SunSailLogic.calcTopPoleExtension(sunSail, poleIndex) } });
      }
    }
  }
  static addAxleArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    const sunSailType = sunSail.sunSailType;
    const pylonCount = SunSailLogic.calcPylonCount(sunSail);
    const poleCount = Poles.getCount(sunSail);

    let systemArticle: IRuleArticle | undefined;
    if (AxleLogic.hasAxle(sunSailType)) { // 765
      const axleLengthSale = SunSailLogic.calcAxleLengthSale(sunSail);
      const axleArticleBase = SunSailLogic.getAxleSystemBaseArticleId(sunSailType, Poles.getCount(sunSail), sunSail.version, pylonCount, sunSail.axleColor);

      const varianten: BeosVarianten = {};
      if (SST.is(sunSailType, SST.XXs)) {
        const variant68 = SunSailLogic.calcVariant68(sunSail);
        if (variant68)
          varianten["68"] = variant68;
      }

      const additionalArticleInfo = CalcSailLogic.calcAdditionalArticleInfoForSystem(sunSail, slc.lc);

      if (sunSailType === SST.Cs) {
        systemArticle = CalcProductLogic.addRuleArticle(slc, axleArticleBase + StringUtil.pad("" + axleLengthSale, 1, "0"), { count: 1, idCategory: "axle", beosOrder: { varianten }, additionalArticleInfo });
      } else if (sunSailType === SST.Xs) {
        const type = poleCount === 3 ? 1 : 2;
        systemArticle = CalcProductLogic.addRuleArticle(slc, axleArticleBase + type + StringUtil.pad("" + axleLengthSale, 1, "0"), { count: 1, idCategory: "axle", beosOrder: { varianten }, additionalArticleInfo });
      } else if (sunSailType === SST.Twin) {
        systemArticle = CalcProductLogic.addRuleArticle(slc, axleArticleBase + StringUtil.pad("" + axleLengthSale, 1, "0"), { count: 1, idCategory: "axle", beosOrder: { varianten }, additionalArticleInfo });
      } else if (sunSailType === SST.Flex) {
        systemArticle = CalcProductLogic.addRuleArticle(slc, "79V1FLEXAWS", { count: 1, idCategory: "windingSystem", partList: { list: "none" }, beosOrder: { varianten }, additionalArticleInfo });
      } else {
        const partList: IPartListProperties | undefined = SST.is(sunSailType, SST.A, SST.One) ? { list: "hardwareList", group: PL_AXLE_GROUP_NAME } : undefined;
        systemArticle = CalcProductLogic.addRuleArticle(slc, axleArticleBase + StringUtil.pad("" + axleLengthSale, 2, "0"), { count: 1, idCategory: "axle", partList, beosOrder: { varianten }, additionalArticleInfo });
      }

      CalcProductLogic.addRuleArticle(slc, "ropeConserver", { count: 0, articleType: "optional" });

    }

    if (systemArticle) {
      CalcSail3dLogic.addRevolvingRopeArticles(slc, sunSail, systemArticle);
    }
  }

  static addTwistingSetArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    // **** TwistingSet 1345
    const twistingSet = SunSailLogic.hasTwistingSetForSunSail(sunSail);
    if (twistingSet) {
      const axleLengthSale = SunSailLogic.calcAxleLengthSale(sunSail);
      const articleNumberAxleLength = "VPVS" + StringUtil.pad("" + axleLengthSale, 3, "0");
      CalcProductLogic.addRuleArticle(slc, articleNumberAxleLength, { count: 1, idCategory: "twistingSet" });  // Verspannungsset /*const twistingSetArticle = */
      const articleNumberParts = SST.is(sunSail.sunSailType, SST.One) ? "VPVSTEILEONE" : "VPVSTEILE"; // https://plaspack.atlassian.net/browse/API-6
      CalcProductLogic.addRuleArticle(slc, articleNumberParts, { count: 1 }) // https://plaspack.atlassian.net/browse/APP22-858

      for (const poleId of [PoleIndex.A, PoleIndex.C]) {
        const poleType = SunSailLogic.getPoleType(sunSail, poleId);
        if (poleType === PoleType.Pole) {
          const mount = SunSailLogic.getTwistingSetMountArticle(sunSail, poleId);
          if (mount) {
            const poleLength = SunSailLogic.getPoleLength(sunSail, poleId);
            // const offerList: OfferListType = (isRingschraubenArticleId(mount.articleNumber) && poleIndex === PoleIndex.A) ? "KH" : "H";  // soll irgendwann verrechnet werden und wegkommen => "H"... https://plaspack.atlassian.net/browse/APP22-871   https://plaspack.atlassian.net/browse/APP-871
            const offerList: OfferListType = "H";
            CalcProductLogic.addRuleArticle(slc, mount.articleNumber, { affiliation: { poleId }, idCategory: "twistingSet", articleGroup: "twistingSet", offerList, computed: { polePosition: poleLength - SunSailLogic.calcTopMountY(sunSail, poleId) + SunSailLogic.calcTopPoleExtension(sunSail, poleId) } });
          }
        }

        if (poleType === PoleType.Wall) {
          if (sunSail.sunSailType === SunSailType.SolidayOne && poleId === PoleIndex.C && ONE_AXLE_WALL_PLATES.includes(SunSailLogic.getWallPlateArticle(sunSail, poleId))) {
            // https://plaspack.atlassian.net/browse/API-67
            CalcProductLogic.addRuleArticle(slc, "00E13000026", { affiliation: { poleId }, idCategory: "twistingSet", articleGroup: "twistingSet", offerList: "H", });
          }
        }
      }
    }
  }

  static addFrachtForPoles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    const getCalculatedPoleLengthFn = PoleChargeOptimizer.calcPoleLengthInfoFn(sunSail);
    if (slc.company?.countryCode === "A" || slc.company?.countryCode === "D") {
      let maxLength = 0;
      for (const poleId of Poles.getPoleIds(sunSail)) {
        if (SunSailLogic.getPoleType(sunSail, poleId) === PoleType.Pole) {
          maxLength = Math.max(maxLength, getCalculatedPoleLengthFn(poleId).totalLength);
        }
      }
      if (maxLength >= 5.005)
        CalcProductLogic.addFracht(slc, 6, "H");
      else if (maxLength >= 4.05)
        CalcProductLogic.addFracht(slc, 5, "H");
    }
  }

  static addMotorArticles(slc: ICalcContext, sunSail: ISunSailData): void {
    const sunSailType = sunSail.sunSailType;
    // **** Additional artciles: 1443
    if (MotorLogic.hasMotor(sunSail.sunSailType)) {
      const granularity = MotorLogic.getMotorCount(sunSailType);
      const motorArticle = SunSailLogic.getMotorArticle(sunSail);
      if (motorArticle) {
        const includedInSystemPrice = motors[motorArticle]?.includedInSystemPrice;
        CalcProductLogic.addRuleArticle(slc, motorArticle, { count: granularity, granularity, quickCategory: "motor", idCategory: "motor", offerList: includedInSystemPrice ? "N" : undefined, partList: { group: "Motor" } }); // Aufpreis f. Funkmotor C12 XL80 LZ 8 Min.
      }

      CalcProductLogic.addRuleArticle(slc, SunSailLogic.getMotorCable(sunSail), { partList: { list: "systemList", group: "Motor" }, count: granularity })
      CalcSailLogic.addRemoteArticles(slc, sunSailType, SunSailLogic.calcRemoteChannelCount(sunSail));
      // CalcSailLogic.addRuleArticle(slc, "00E13000226", { count: 0, articleType: "optional", quickCategory: "motor" }); // Kleber Set für Soliday System C/CS // bei  der neuen Welle nicht mehr benötigt https://plaspack.atlassian.net/browse/APP22-267
      // CalcSailLogic.addRuleArticle(slc, "00E13000253", { count: 0, articleType: "optional", quickCategory: "motor" }); // Spezialpistole f. Schraubenkleber  // bei  der neuen Welle nicht mehr benötigt

      if (!SunSailLogic.getMotorRemoteType(sunSail)) {
        // CalcSailLogic.addRuleArticle(slc, "71002A005", { articleType: "optional", quickCategory: "motorAdditional" }) // wird nicht mehr produziert: https://plaspack.atlassian.net/browse/APP22-924
        CalcProductLogic.addRuleArticle(slc, "71002A006", { articleType: "optional", idCategory: "motor", quickCategory: "motorAdditional", partList: { group: "Motor" } })
      }
    }
  }

  static addCleanerArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    const sunSailType = sunSail.sunSailType;

    if (SST.is(sunSailType, SST.X)) { // glue - but almost a cleaner ;-)
      CalcProductLogic.addRuleArticle(slc, "00E13000226", { count: 0, articleType: "optional" }); // Kleber Set für Soliday System C/CS
    }
    CalcProductLogic.addRuleArticle(slc, "algaeRemover", { count: 0, articleType: "optional" })

    const clothType = SunSailLogic.getCloth(sunSail)?.clothType;
    if (!ClothData.isAustronet(clothType)) {
      CalcProductLogic.addRuleArticle(slc, "00E13001011", { count: 0, articleType: "optional" }); //Soliday Cleaner Oxidationreiniger
      CalcProductLogic.addRuleArticle(slc, "00E13001010", { count: 0, articleType: "optional" }); //Soliday Cleaner Imprägniermittel
    }
    CalcProductLogic.addRuleArticle(slc, "00E13001986", { count: 0, articleType: "optional" }); // Soliday Aluminium-Reiniger 500 ml Pet-Flasche mit Sprayer
    CalcProductLogic.addRuleArticle(slc, "00E13001987", { count: 0, articleType: "optional" }); // Soliday Edelstahl-Reiniger 500 ml Pet-Flasche mit Sprayer
    CalcProductLogic.addRuleArticle(slc, "00E13001989", { count: 0, articleType: "optional" }); // ALU POLISH - Eloxalreiniger Tube 100 ml

    // *** CleaningSet 1582
    if (sunSail.sourceConfigurator !== "sc") {
      const cleaningSetCount = CalcSail3dLogic.calcCleaningSetCount(sunSail);
      if (SST.is(sunSailType, SST.CsTwin, SunSailType.SolidayC))
        CalcProductLogic.addRuleArticle(slc, "00E13001325", { count: cleaningSetCount, articleType: "optional" }); // Soliday Reinigungs- und Pflegeset für Soliday-C/CS/CS-Twin
      else
        CalcProductLogic.addRuleArticle(slc, "00E13001321", { count: cleaningSetCount, articleType: "optional" }); // Soliday Reinigungs- und Pflegeset für Soliday-MA/M/X/XS
    }
  }

  static addAxleDrillTemplateArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    const sunSailType = sunSail.sunSailType;
    if (SST.is(sunSailType, SST.C)) {
      CalcProductLogic.addRuleArticle(slc, "00E13001880", { count: 0, articleType: "optional", quickOverlayPriority: QuickOverlayPriority.DrillTemplate }); // Bohrschablone Welle D90
    } else if (SST.is(sunSailType, SST.CsTwin)) {
      CalcProductLogic.addRuleArticle(slc, "00E13001881", { count: 0, articleType: "optional", quickOverlayPriority: QuickOverlayPriority.DrillTemplate }); // Bohrschablone Welle D90
    } else if (SST.is(sunSailType, SST.A)) {
      if (Pvi.is(sunSail.version, "2024"))
        CalcProductLogic.addRuleArticle(slc, "00E13002062", { count: 0, articleType: "optional", quickOverlayPriority: QuickOverlayPriority.DrillTemplate }); // Bohrschablone Welle Ø65
      else
        CalcProductLogic.addRuleArticle(slc, "00E13001156", { count: 0, articleType: "optional", quickOverlayPriority: QuickOverlayPriority.DrillTemplate }); // Bohrschablone Welle Ø50
    } else if (SST.is(sunSailType, SST.One)) {
      CalcProductLogic.addRuleArticle(slc, "00E13002062", { count: 0, articleType: "optional", quickOverlayPriority: QuickOverlayPriority.DrillTemplate }); // Bohrschablone Welle Ø65
    }
  }


  static checkAxle(slc: ICalcContext, sunSail: ISunSailData): void {
    const sunSailType = sunSail.sunSailType;

    if (AxleLogic.hasAxle(sunSailType)) {
      const lc = slc.lc;
      const poleCount = Poles.getCount(sunSail);
      const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, undefined);

      const topAxleAngle = SunSailLogic.calcAxleAngleDegrees(sunSailType, mountPositions);

      const logicalAxleLength = SunSailLogic.calcAxleLength(sunSail, "logical");
      const { min, max } = SunSailLogic.getLogicalMinMaxAxle(sunSail);
      if (gtc(logicalAxleLength, max, 2) || ltc(logicalAxleLength, min))
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "axle", lc.t("Rule_AxleLengthMinMax"), { params: [min, max, logicalAxleLength.toFixed(2)]})

      const angle = -topAxleAngle;
      if (sunSailType === SunSailType.SolidayC && ltc(angle, 0, 2)) {
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "axle", lc.t("Rule_AxleAngleMotor"), { params: [0, angle.toFixed(2)]});
      } else if (sunSailType === SunSailType.SolidayXS && gtc(Math.abs(angle), 5, 2)) {
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "axle", lc.t("Rule_AxleAngleMinMax"), { params: [-5, 5, angle.toFixed(2)]});
      } else if (sunSailType === SunSailType.SolidayCS || sunSailType === SunSailType.SolidayTwin) {
        if (poleCount === 3) {
          if ((sunSail.motorPosition === PoleIndex.A && ltc(angle, 0, 2)) || (sunSail.motorPosition === PoleIndex.B && gtc(angle, 0, 2)))
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "axle", lc.t("Rule_AxleAngleMotor"), { params: [5, angle.toFixed(2)]})
        } else {
          const angle = -topAxleAngle;
          if (ltc(angle, 0, 2) || gtc(angle, 10, 2)) // https://plaspack.atlassian.net/browse/APP22-946
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "axle", lc.t("Rule_AxleAngleMinMax"), { params: [0, 10, angle.toFixed(2)]})
        }
      } else if (sunSailType === SunSailType.SolidayOne) {
        // if (SunSailLogic.getPoleType(sunSail, PoleIndex.A) === PoleType.Pole)
        {
          const poleAngle = MathUtil.rad2deg(SunSailLogic.getPoleAngle(sunSail, PoleIndex.A));
          const totalAngle = -(poleAngle + angle);
          const minAngle = -10;
          const maxAngle = 5;
          if (ltc(totalAngle, minAngle, 2) || gtc(totalAngle, maxAngle, 2))
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, "axle", lc.t("Rule_PoleAxleAngle"), { params: [getPointName(PoleIndex.A), minAngle, maxAngle, totalAngle.toFixed(2)]});
        }
      }

      if (SST.is(sunSailType, SST.CsXsTwin)) {
        if (poleCount >= 4) {
          const axleAngle = -SunSailLogic.calcMountAngleDegree(mountPositions, 2, 3);
          const maxAngle = 10;
          const angle = Math.abs(axleAngle - topAxleAngle);
          if (angle > maxAngle)
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "cd", lc.t("Rule_CsXsPylonAngle"), { params: [maxAngle.toFixed(2), angle.toFixed(2)]});
        }
        if (poleCount === 6) {
          const axleAngle = -SunSailLogic.calcMountAngleDegree(mountPositions, 4, 5);
          const maxAngle = 10;
          const angle = Math.abs(axleAngle - topAxleAngle);
          if (angle > maxAngle)
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "ef", lc.t("Rule_CsXsPylonAngle"), { params: [maxAngle.toFixed(2), angle.toFixed(2)]});
        }
      }

      if (sunSailType === SST.C) {
        const ret = SunSailLogic.calcAngleBetweenSailGroups(sunSailType, mountPositions);
        const angleABC2ACD = MathUtil.rad2deg(ret.angle);
        const angle = ret.type === "V" ? 120 : logicalAxleLength < 9 ? 150 : 170; // https://plaspack.atlassian.net/jira/software/projects/APP/issues/APP-1349
        if (angleABC2ACD < angle)
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "axle", lc.t("Rule_AngleSails"), { params: [logicalAxleLength.toFixed(2), angle, angleABC2ACD.toFixed(2)]})
      }

      // RulesChecker 505
      if (sunSailType === SST.X) {
        const ret = SunSailLogic.calcAngleBetweenSailGroups(sunSailType, mountPositions);
        const angleABC2ACD = MathUtil.rad2deg(ret.angle);
        const angle = ret.type === "V" ? 120 : 170; // https://plaspack.atlassian.net/jira/software/projects/APP/issues/APP-1349
        if (angleABC2ACD < angle)
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "axle", lc.t("Rule_AngleSails"), { params: [logicalAxleLength.toFixed(2), angle, angleABC2ACD.toFixed(2)]})
      }
    }
  }

  static checkSolidayC(slc: ICalcContext, sunSail: ISunSailData): void {
    const sunSailType = sunSail.sunSailType;

    // **** Soliday C rules 619
    if (sunSailType === SST.C) {
      const lc = slc.lc;
      const poleCount = Poles.getCount(sunSail);
      const clothType = sunSail.cloth?.clothType;
      const pylonCount = SunSailLogic.calcPylonCount(sunSail);
      const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, undefined);
      const netPositionGroups = SunSailLogic.calcNetPositionGroups(sunSail, {});
      const sunSailArea = SunSailLogic.calcSunSailAreaCloth(sunSail);

      const topAxlePointsDistance = SunSailLogic.calcTopAxlePointsDistance(sunSailType, mountPositions);
      // **** Soliday C - Soltis
      if (clothType === ClothType.Soltis86 || clothType === ClothType.Soltis92) {
        if (gtc(topAxlePointsDistance, 1))
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "ac", lc.t("Rule_SolitsMaxZ"),{ params: [1, topAxlePointsDistance.toFixed(2)]});

        const axleCenter = Vec3.mid(mountPositions[0], mountPositions[2]);
        const distFromCenterB = Vec3.len(SunSailMath.calcNearestAxlePoint(mountPositions[0], mountPositions[2], mountPositions[1]), axleCenter);
        if (gtc(distFromCenterB, 1))
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "B", lc.t("Rule_SoltisMaxDistCenter"), { params: [1, distFromCenterB.toFixed(2)]});
        const distFromCenterD = Vec3.len(SunSailMath.calcNearestAxlePoint(mountPositions[0], mountPositions[2], mountPositions[3]), axleCenter);
        if (gtc(distFromCenterD, 1))
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "D", lc.t("Rule_SoltisMaxDistCenter"), { params: [1, distFromCenterD.toFixed(2)]});
      }

      // ***** Soliday C - one pylon
      if (pylonCount === 1) {
        if (gtc(sunSailArea, 75))
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "net", lc.t("Rule_C_OnePylonSunSailArea"), { params: [75, sunSailArea.toFixed(2)]});
        CalcSail3dLogic.checkSideLength(slc.errors, sunSailType, poleCount, netPositionGroups, lc.t("Rule_C_OnePylonSideLength"), 9);

        const factorZ = SunSailLogic.calcFactorZ(sunSail);
        if (factorZ > 20)
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "axle", lc.t("Rule_C_OnePylonZ"), { params: [20, factorZ.toFixed(0)]})

        const rayAC = Ray3.fromPoints(mountPositions[0], mountPositions[2]);
        const rayBD = Ray3.fromPoints(mountPositions[1], mountPositions[3]);
        const points = Ray3.calcClosestPointOnTwoRays(rayAC, rayBD);
        const deltaH = (points ? points[1].y - points[0].y /*Vec3.len(points[0], points[1])*/ : 0); // cm // https://plaspack.atlassian.net/browse/APP-1486
        if (ltc(deltaH, 0.1, 3))
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, "axle", lc.t("Rule_C_OnePylonDeltaH"), { params: [10, deltaH.toFixed(0)]})

        if (SunSailLogic.getPolePylonType(sunSail, PoleIndex.A))
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "A", lc.t("Rule_C_OnePylonAllowedPoints"))
        if (SunSailLogic.getPolePylonType(sunSail, PoleIndex.C))
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "C", lc.t("Rule_C_OnePylonAllowedPoints"))
      } else if (pylonCount === 2) {
        if (gtc(sunSailArea, 85))
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "net", lc.t("Rule_C_TwoPylonSunSailArea"), { params: [85, sunSailArea.toFixed(2)]});
        CalcSail3dLogic.checkSideLength(slc.errors, sunSailType, poleCount, netPositionGroups, lc.t("Rule_C_TwoPylonSideLength"), 10);
      } else {
        // CalcSailLogic.addError(errors, "product", _t.Rule_C_OnePylonRequired);
      }
    }
  }

  static checkPoint(slc: ICalcContext, readOnlyArticles: Readonly<RuleArticleMap>, ssd: ISunSailData, poleId: PoleId): void {
    const lc = slc.lc;
    const sunSailType = ssd.sunSailType;
    const poleCount = Poles.getCount(ssd);

    const angleDeg = MathUtil.rad2deg(SunSailLogic.getPoleAngle(ssd, poleId));
    const distOut = SunSailLogic.calcDistanceOut(ssd, poleId);
    const distIn = SunSailLogic.calcDistancesIn(ssd, poleId);
    const poleLength = SunSailLogic.getPoleLength(ssd, poleId);
    const poleLengthB = SunSailLogic.getPoleLengthB(ssd, poleId);
    const poleType = SunSailLogic.getPoleType(ssd, poleId);
    const pylonType = SunSailLogic.getPolePylonType(ssd, poleId)
    const poleMaterial = SunSailLogic.getPoleMaterial(ssd, poleId);
    const poleAngle = SunSailLogic.getPoleAngle(ssd, poleId)
    const poleMount = SunSailLogic.getPoleMount(ssd, poleId);
    const allowedLengthB = PoleLogic.getAllowedLengthB(sunSailType, poleType, poleMaterial, poleMount, poleLengthB > 0, poleAngle);
    const heightChange = SunSailLogic.getHeightChangeArticle(ssd, poleId);

    const mountPositions = SunSailLogic.calcMountPositions(ssd, 0, undefined);
    const getCalculatedPoleLength = PoleChargeOptimizer.calcPoleLengthInfoFn(ssd);
    const allowedAngles = SunSailLogic.getAllowedPoleAngles(ssd, poleId);

    const pylonCount = SunSailLogic.calcPylonCount(ssd);
    const heightChangeCount = SunSailLogic.calcHeightChangeCount(ssd);

    const amplifier = AmplifierLogic.getAmplifierArticleWithSelfResponsibility(ssd, poleId);
    if (amplifier === "__self_responsibility__") {
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), slc.lc.t("Rule_Amplifier_Self_Responsibility"))
    }

    const ropeFixArticle = SunSailLogic.hasRopeFixArticle(ssd, poleId);
    if (ropeFixArticle) {
      const coilUpHeightMinMax = SunSailLogic.calcRopeFixHeightMinMax(ssd, poleId);

      const coh = SunSailLogic.getRopeFixHeight(ssd, poleId);
      if (ltc(coh, coilUpHeightMinMax.min, 2))
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_Winch_MinHeight"));
      if (gtc(coh, coilUpHeightMinMax.max, 2))
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_Winch_MaxHeight"));
    }

    if (sunSailType === SST.Xs && poleCount === 4) {
      // 966
      if (ropeFixArticle && PoleLogic.isAxle(sunSailType, poleId)) {
        const dist = LogicS.calcRopeDistFromNet_XS(ssd, poleId, mountPositions);
        if (dist < 0.005)
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_XS_Indirect"));
      }
    }

    if (poleType === PoleType.Pole && pylonType && PoleLogic.isDroppole(poleMaterial)) {
      if (poleMount === PoleMountType.EarthScrew || poleMount === PoleMountType.GroundPlate) {
        const minHeight = SunSailLogic.getPylonDornLength(poleMaterial, poleMount) + SunSailLogic.getPylonInfo(pylonType).length; // 0.5 dornlength
        if (ltc(poleLength, minHeight, 2))
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, getPointName(poleId), lc.f("Rule_Droppole_Plate_MinHeight", minHeight));
      }
      if (SunSailLogic.isDroppoleCut(poleLength)) {
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Hint, getPointName(poleId), slc.lc.t("Rule_Droppole_Cut_Warning"), { params: MathUtil.round(poleLength, 2)})
      }
    }


    if (poleType === PoleType.Pole && poleMount === PoleMountType.None) {
      if (!PoleLogic.isIron(poleMaterial)) { // no mount type for iron poles...
        CalcProductLogic.addError(slc.errors, SST.is(sunSailType, SST.CoSF) ? ProjectCheckErrorType.Hint : ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_Pole_NoMount"));
      }
    }


    // ***** HeightChange 10006
    switch (sunSailType) {
      case SunSailType.SolidayC: {
        if (heightChange) {
          if (pylonCount !== 2)
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_C_HeightChange_TwoPylons"));
          if (!pylonType)
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_C_HeightChange_Pylon"));
          if (heightChangeCount >= 2)
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, getPointName(poleId), lc.t("Rule_C_HeightChange_MaxCount"));
        }
        break;
      }
    }

    if (HeightChangeLogic.hasHeightChangeMotor(heightChange)) {
      const axleWireless = !!SunSailLogic.getMotorRemoteType(ssd);
      const heightChangeWireless = !!HeightChangeLogic.getHeightChangeRemoteType(heightChange);
      if (axleWireless !== heightChangeWireless)
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_Wireless_All"));
    }

    if (ropeFixArticle) {
      if (SST.is(sunSailType, SST.AMX)) { // , SST.Flex, SST.One Flex und One werden über Punkt A eingerollt/ausgerollt - beim Flex kann man bei B, C und D nur spannen?!? https://plaspack.atlassian.net/browse/API-57
        if (poleCount === 4) {
          const oppositePole = poleId === PoleIndex.B ? PoleIndex.D : PoleIndex.B;
          const distOutOpposite = SunSailLogic.calcDistanceOut(ssd, oppositePole);
          if (ltc(distOut, distOutOpposite, 2))
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_DistOut_CoilUp"), { params: [distOut.toFixed(2), distOutOpposite.toFixed(2)] });
        }

        if (SunSailLogic.calcPossibleCoilUps(ssd, poleId).length === 0) {
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_CoilUp_Valid_Pole"));
        }
      }
    }

    if (heightChange && !SunSailLogic.isHeightChangeValid(ssd, poleId))
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_HeighChange_Valid_Pole"));

    if (poleMount === PoleMountType.Clamp) {
      const possibleMountClamps = SunSailLogic.calcPossiblePoleMountClamps(poleMount, poleMaterial, poleType);
      const foundClamps = RuleArticleHelper.countArticles(readOnlyArticles, possibleMountClamps.articles.map((a) => a.articleNumber), poleId)
      if (foundClamps.totalCount === 0)
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_PoleClamp_Count_0"));
      else if (foundClamps.totalCount < 2)
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_PoleClamp_Count_1"));
      else if (foundClamps.totalCount > 4)
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_PoleClamp_Count_TooMany"));
    }

    // 1200
    if (SST.is(sunSailType, SST.AMCXOne, SST.CsXsTwin)) {
      const maxDistanceOut = SunSailLogic.getMaxAllowedDistOut(sunSailType, SunSailLogic.getInfluencingIndirectPylonType(ssd, poleId));
      if (gtc(distOut, maxDistanceOut, 2))
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_MaxDistOut"), { params:  [maxDistanceOut, distOut.toFixed(2)]});
    }


    // **** DistIn/DistOut 1207
    {
      if (AxleLogic.hasAxle(sunSailType)) {
        const nettoAxleLength = SunSailLogic.calcAxleLength(ssd, "logical");
        if (PoleLogic.isAxle(sunSailType, poleId)) {
          const maxAllowedDistanceIn = SunSailLogic.getMaxAllowedDistInAxle(sunSailType, nettoAxleLength).soft;
          for (const di of distIn.array) {
            if (gtc(di, maxAllowedDistanceIn, 2))
              CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_MaxDistIn_Axle_Soft"), { params: [maxAllowedDistanceIn.toFixed(2), di.toFixed(2)]});
          }
        } else {
          const maxAllowedDistanceIn = SunSailLogic.getMaxAllowedDistInNonAxle(sunSailType);
          const maxAllowedDistanceInWarning = SunSailLogic.getMaxAllowedDistInNonAxleWarning(sunSailType, poleCount, poleId);
          const di = distIn.array[0];
          if (gtc(di, maxAllowedDistanceIn, 2))
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_MaxDistIn"), { params: [maxAllowedDistanceIn, di.toFixed(2)] });
          else if (gtc(di, maxAllowedDistanceInWarning, 2)) { // https://plaspack.atlassian.net/browse/APP22-948
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_MaxDistIn_Warning"), { params: [maxAllowedDistanceInWarning, di.toFixed(2)]});
          }
        }
      }

      for (const di of distIn.array) {
        if (di < -0.0001)
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_MinDistIn"), { params: [0, di.toFixed(2)]});
      }

      if (!PoleLogic.isAxle(ssd.sunSailType, poleId)) {  // https://plaspack.atlassian.net/browse/APP-1354
        if (distOut < -0.0001)
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_MinDistOut"), { params: [0, distOut.toFixed(2)]});
      }
    }

    if (angleDeg >= 90)
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_MaxPoleAngle"), { params: [90, angleDeg.toFixed(2)]});

    if (gtc(poleLengthB, poleLength, 2))
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_MaxLengthB_Pole"), { params: [poleLengthB.toFixed(2), poleLength.toFixed(2)]});

    // 1237
    if (pylonType) {
      const pylonLength = SunSailLogic.getPylonInfo(pylonType).length
      const lengthA = SunSailLogic.getPoleLengthA(ssd, poleId);
      if (SunSailLogic.isPoleBent(ssd, poleId)) {
        const minLength = pylonLength + PoleLogic.calcMinBendLength(poleMaterial, poleAngle);
        if (gtc(minLength, lengthA, 2))
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_MinPylonLength_Bend"), { params: [minLength.toFixed(2), lengthA.toFixed(2)]});
      } else {
        const minLength = pylonLength;
        if (minLength > poleLength) {
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_MinPylonLength"), { params: [minLength.toFixed(2), lengthA.toFixed(2)]});
        }

        const dornLength = SunSailLogic.getPylonDornLength(poleMaterial, poleMount)
        const bottomPoleExtension = SunSailLogic.calcBottomPoleExtension(ssd, poleId);
        if (ltc(poleLength - /*poleOffsetY -*/ dornLength, pylonLength, 2)) {
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, getPointName(poleId), lc.t("Rule_Pylon_TooLong"), { params: [pylonLength.toFixed(2), lengthA.toFixed(2)]});
        } else if (ltc(poleLength - /*poleOffsetY -*/ bottomPoleExtension - dornLength, pylonLength, 2)) {
          if (SunSailLogic.getPoleMount(ssd, poleId) !== PoleMountType.ConcreteCube)
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_Pylon_OverGround"), { params: [minLength.toFixed(2), lengthA.toFixed(2)]});
        }

      }
    }

    // **** Pole rules: 1251
    if (poleType === PoleType.Pole) {
      const pli = getCalculatedPoleLength(poleId);
      const maxPoleLength = PoleLogic.getMaxPoleLength(poleMaterial);
      if (gtc(pli.totalLength, maxPoleLength, 2))
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_Pole_MaxLength"), { params: [maxPoleLength, pli.totalLength.toFixed(2)]});

      if (!PoleLogic.isPoleMountPossible(poleMaterial, poleMount))
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_PoleMount_Invalid_ForMaterial"));

      // const lengthB = PoleLogic.calcLengthB(pole.lengthB);
      if (poleLengthB > 0) {
        if (allowedLengthB) {
          const lengthBMinMax = PoleLogic.getMinMax(allowedLengthB);
          if (ltc(poleLengthB, lengthBMinMax.min, 2))
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_MinLengthB"), { params: [lengthBMinMax.min.toFixed(2), poleLengthB.toFixed(2)]});
          if (gtc(poleLengthB, lengthBMinMax.max, 2))
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_MaxLengthB"), { params: [lengthBMinMax.max.toFixed(2), poleLengthB.toFixed(2)]});
        }

        // const lengthAMinMax = PoleLogic.getMinMax(poleAttributes.lengthAMin);
        // const lengtha = PoleLogic.calcLengthA(pole.length, pole.lengthB, pole.angle);
        // if (ltc(lengtha, lengthAMinMax.min, 2))
        //   CalcSailLogic.addError(errors, getPoleName(i), "Die Länge über der Biegungsmitte muss mindestens ${0}m betragen. (${1})", [lengthAMinMax.min, lengtha.toFixed(2)]);
        // if (gtc(lengtha, lengthAMinMax.max, 2))
        //   CalcSailLogic.addError(errors, getPoleName(i), "Die Länge über der Biegungsmitte darf maximal ${0}m betragen. (${1})", [lengthAMinMax.max, lengtha.toFixed(2)]);

        const bendable = allowedLengthB;
        if (!bendable)
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_Pole_Bend_Invalid"));

        CalcSail3dLogic.checkMinMaxAngle(angleDeg, allowedAngles, slc.errors, getPointName(poleId), lc.t("Rule_Pole_Bend_Angle"), lc.t("Rule_Pole_Bend_Angles"), lc.t("Rule_Pole_Bend_Angle_Range"));

        if (PoleLogic.isAlu(poleMaterial))
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_Alu_Bend_Warning"))
      } else {
        CalcSail3dLogic.checkMinMaxAngle(angleDeg, allowedAngles, slc.errors, getPointName(poleId), lc.t("Rule_Pole_Angle"), lc.t("Rule_Pole_Angles"), lc.t("Rule_Pole_Angle_Range"));
      }


      // BaseCube 1325
      if (poleMount === PoleMountType.BaseCube) {
        const sunSailArea = SunSailLogic.calcSunSailAreaCloth(ssd);

        if (gtc(poleLength, 3.5, 2))
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_BaseCube_MaxHeight"), { params: 3.5 });

        if (sunSailType === SST.M && PoleLogic.isAxle(sunSailType, poleId))
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_BaseCube_SolidayM_NoAxlePole"));

        if (SST.is(sunSailType, SST.AMOne, SST.XXs)) {
          if (gtc(sunSailArea, 40, 2))
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_BaseCube_MaxSailArea"), { params: [40, sunSailArea]});
        }

        if (SST.is(sunSailType, SST.CsXsTwin, SunSailType.SolidayC)) {
          if (gtc(sunSailArea, 50, 2))
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_BaseCube_MaxSailArea"), { params: [50, sunSailArea]});
        }
      }
    }

    if (poleType === PoleType.Wall) {
      if (pylonType)
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleId), lc.t("Rule_Pylon_PoleOnly"));
    }

    if (SunSailLogic.getPoleMount(ssd, poleId) === PoleMountType.Clamp && poleLengthB > 0 && PoleLogic.isAluOrSteel(SunSailLogic.getPoleMaterial(ssd, poleId)))
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Hint, getPointName(poleId), lc.t("Rule_Info_Clamp_LengthB"));

    // heightChange
    if (SunSailLogic.isSlideBar(SunSailLogic.getHeightChangeArticle(ssd, poleId))) {
      const length = SunSailLogic.calcSlideBarLength(ssd, poleId);
      if (length <= 0.1)
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, getPointName(poleId), slc.lc.t("Rule_HeightChange_NotPossible"));
      // else if (length < 0.45)
      //   CalcSailLogic.addWarning(slc.errors, getPoleName(poleIndex), lc.t("Rule_HeightChange_VeryShort"));
    }
  }

  static addAdditionalPoleArticles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): void {
    const sunSailType = sunSail.sunSailType;
    const sailMountArticle = SunSailLogic.getSailMountArticle(sunSail, poleId);
    CalcProductLogic.addRuleArticle(slc, sailMountArticle, { affiliation: { poleId }, count: 1, articleType: "obligatory", idCategory: "sailMount" });

    const sailMountWinch = SunSailLogic.getSailMountWinch(sunSail, poleId);
    CalcProductLogic.addRuleArticle(slc, sailMountWinch, { affiliation: { poleId }, articleType: "obligatory", idCategory: "sailMount" });
    const sailMountAccessories = SunSailLogic.getSailMountAccessories(sunSail, poleId);
    for (const a of sailMountAccessories)
      CalcProductLogic.addRuleArticle(slc, a.articleId, { affiliation: { poleId }, articleType: a.optional ? "optional" : "obligatory", idCategory: "sailMount" });

    // const sailPulleyArticle = SunSailLogic.getSailPulleyArticle(sunSail, i); // already added with extramount...
    // CalcSailLogic.addRuleArticle(slc, sailPulleyArticle, { keys: { poleId: i, }, count: 1, articleType: "obligatory", idPart: "sailMountPulley", info: "sailPulleyArticle" });
    const sailRopeArticle = SunSailLogic.getSailRopeArticle(sunSail, poleId);
    if (sailRopeArticle) {
      const length = SunSailLogic.calcSailMountRopeLength(sunSail, poleId);
      // partList: { list: "stockList" }sadfsad
      // offerList: "S"
      const offerList = "H"; // https://plaspack.atlassian.net/browse/APPSK-137
      CalcProductLogic.addRuleArticle(slc, sailRopeArticle, { affiliation: { poleId }, count: 1, mul: length, stkUnit: "Stk", mulUnit: "lfm", articleType: "obligatory", idCategory: "sailMount", offerList, partList: { list: "stockList" }, additionalArticleInfo: { length: AdditionalArticleInfo.lengthToMM(slc, length) } });

      if (!FlexLogic.isFlexC(sunSailType, poleId)) {
        CalcProductLogic.addRuleArticle(slc, "00E13000417", { ...CalcSailLogic.calcSailMountShackleOptions(sunSail, poleId), offerList });
      }
      CalcProductLogic.addRuleArticle(slc, RopeHelper.getKauscheForRopeArticle(sailRopeArticle), { ...CalcSailLogic.calcSailMountRopeKauscheOptions(sunSail, poleId), offerList });
    }
  }

  static doInfos3d(c: ICalcContext, sunSail: ISunSailData): void {
    const lc = c.lc;
    // if (isDebug()) {
    //   if (c.infos.length > 0)
    //     throw new Error("Errors found before info section...");
    // }

    const sunSailType = sunSail.sunSailType;
    const poleCount = Poles.getCount(sunSail);
    const topology = SunSailTopology.calcTopology(sunSailType, poleCount);
    const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, undefined);
    const getCalculatedPoleLengthFn = PoleChargeOptimizer.calcPoleLengthInfoFn(sunSail);

    // Infos
    if (AxleLogic.hasAxle(sunSailType)) {
      const logicalAxleLength = SunSailLogic.calcAxleLength(sunSail, "logical");
      CalcProductLogic.addInfo(c.infos, lc.t("Info_AxleLength"), Measure.toMM(logicalAxleLength), { group: "Axle" })
      const axleDegrees = -SunSailLogic.calcAxleAngleDegrees(sunSail.sunSailType, mountPositions);
      CalcProductLogic.addInfo(c.infos, lc.t("Info_AxleAngle"), axleDegrees.toFixed(1) + "°", { group: "Axle" })
    }

    if (SST.is(sunSail.sunSailType, SST.Sandy)) {
      CalcProductLogic.addInfo(c.infos, lc.t("ArticleInfo_ClothSourceArea"), SandyLogic.calcSandyObbArea(sunSail)!.area.toFixed(2) + Measure.m2, { group: "SunSail", access: Access.Monteur });
    }
    CalcProductLogic.addInfo(c.infos, lc.t("Info_SailArea"), SunSailLogic.calcSunSailAreaCloth(sunSail).toFixed(2) + Measure.m2, { group: "SunSail" })

    { // Niveau
      const values = mapCount(Poles.getCount(sunSail), (i) => {
        return { header: getPointName(i), value: Measure.toMM(SunSailLogic.getPoleNiveau(sunSail, i)) };
      });
      CalcProductLogic.addInfo(c.infos, lc.t("Info_Niveau"), values, { group: "Niveau" })
    }

    { // angle
      const values = sunSail.poles.map((_p, i) => {
        const poleAngle = SunSailLogic.getPoleAngle(sunSail, i);
        return { header: getPointName(i), value: MathUtil.rad2deg(poleAngle).toFixed(1) };
      });
      CalcProductLogic.addInfo(c.infos, lc.t("Info_Angle"), values, { group: "Angle" })
    }

    { // LengthB
      const values = sunSail.poles.map((_p, i) => {
        const poleLengthB = SunSailLogic.getPoleLengthB(sunSail, i);
        return { header: getPointName(i), value: Measure.toMM(poleLengthB) };
      });
      CalcProductLogic.addInfo(c.infos, lc.t("Info_LengthB"), values, { group: "LengthB" })
    }

    { // Length
      const values = sunSail.poles.map((_p, i) => {
        const poleLength = SunSailLogic.getPoleLength(sunSail, i);
        return { header: getPointName(i), value: Measure.toMM(poleLength) };
      });
      CalcProductLogic.addInfo(c.infos, lc.t("Info_Length"), values, { group: "Length" })
    }

    { // Gesamtlänge
      // const poleLengthInfos = SunSailLogic.calcPoleLengthInfos(sunSail);
      {
        const values = sunSail.poles.map((_p, i) => {
          return { header: getPointName(i), value: Measure.toMM(getCalculatedPoleLengthFn(i).totalLength) };
        });
        CalcProductLogic.addInfo(c.infos, lc.t("Info_Length_Total"), values, { group: "totalLength" })
      }
      {
        const values = sunSail.poles.map((_p, i) => {
          return { header: getPointName(i), value: Measure.toMM(getCalculatedPoleLengthFn(i).shoppingLengthCharged) };
        });
        CalcProductLogic.addInfo(c.infos, lc.t("Info_Length_Charged"), values, { group: "chargedLength" })
      }
      {
        const values = sunSail.poles.map((_p, i) => {
          return { header: getPointName(i), value: Measure.toMM(getCalculatedPoleLengthFn(i).bottomAdd) };
        });
        CalcProductLogic.addInfo(c.infos, lc.t("Info_Length_BottomExtension"), values, { group: "bottomExtension" })
      }
    }

    { // DistanceIn
      const values = sunSail.poles.map((_p, i) => {
        const distIn = SunSailLogic.calcDistanceIn(sunSail, i);
        return { header: getPointName(i), value: Measure.toMM(distIn) };
      });
      CalcProductLogic.addInfo(c.infos, lc.t("Info_DistIn"), values, { group: "DistIn" });
    }

    { // MaxDistanceIn
      const values = sunSail.poles.map((_p, i) => {
        const maxDistInOut = SunSailLogic.calcMaxDistInOut(sunSail, i);
        return { header: getPointName(i), value: Measure.toMM(maxDistInOut.maxDistIn) };
      });
      CalcProductLogic.addInfo(c.infos, lc.t("Info_DistIn_Max"), values, { group: "MaxDistIn" });
    }

    // DistanceOut
    if (AxleLogic.hasAxle(sunSail.sunSailType)) {
      const values = sunSail.poles.map((_p, i) => {
        const distOut = SunSailLogic.calcDistanceOut(sunSail, i);
        return { header: getPointName(i), value: (PoleLogic.isAxle(sunSail.sunSailType, i) ? "-" : Measure.toMM(distOut)) };
      });
      CalcProductLogic.addInfo(c.infos, lc.t("Info_DistOut"), values, { group: "DistOut" })
    }

    { // PoleType
      const values = sunSail.poles.map((_p, i) => {
        const poleType = SunSailLogic.getPoleType(sunSail, i);
        return { header: getPointName(i), value: lc.t(LocaContext.connect("PolePanel", poleType)) };
      });
      CalcProductLogic.addInfo(c.infos, lc.t("Info_Pole_Type"), values, { group: "PoleType" })
    }

    { // Mastmaterial
      const values = sunSail.poles.map((_p, i) => {
        return { header: getPointName(i), value: lc.t(LocaContext.connect("Material", SunSailLogic.getPoleMaterial(sunSail, i))) };
      });
      CalcProductLogic.addInfo(c.infos, lc.t("Info_Pole_Material"), values, { group: "material", maxPerRow: 4 })
    }

    { // PoleMount
      const values = sunSail.poles.map((_p, i) => {
        const poleMount = SunSailLogic.getPoleMount(sunSail, i);
        return { header: getPointName(i), value: (poleMount !== PoleMountType.None ? lc.t(LocaContext.connect("PoleMount", poleMount)) : "-") };
      });
      CalcProductLogic.addInfo(c.infos, lc.t("Info_Pole_Mount"), values, { group: "PoleMount", maxPerRow: 4 })
    }

    { // Pylon
      const values = sunSail.poles.map((_p, i) => {
        const pylonType = SunSailLogic.getPolePylonType(sunSail, i)
        return { header: getPointName(i), value: (pylonType ? "✓" : "-") };
      });
      CalcProductLogic.addInfo(c.infos, lc.t("Info_Pole_Pylon"), values, { group: "pylon" })
    }

    { // HeightChange
      const values = sunSail.poles.map((_p, i) => {
        return { header: getPointName(i), value: (SunSailLogic.getHeightChangeArticle(sunSail, i) ? "✓" : "-") };
      });
      CalcProductLogic.addInfo(c.infos, lc.t("Info_Pole_HeightChange"), values, { group: "HeightChange" })
    }

    { // Seitenlängen
      // const topology = SunSailTopology.calcTopology(sunSail.sunSailType, Poles.getPoleCount(sunSail).corner)
      const sides = topology.sides;
      const ground: IProjectInfoValue = [];
      const mount: IProjectInfoValue = [];
      for (const s of sides) {
        if (CalcSail3dLogic.isSideVisible(sunSailType, poleCount, s)) {
          {
            const l = Vec3.len(SunSailLogic.getSideVector(sunSail, s.first, s.second));
            ground.push({ header: getSideName(s.first, s.second), value: Measure.toMM(l) });
          }
          {
            const l = Vec3.len(mountPositions[s.first], mountPositions[s.second]);
            mount.push({ header: getSideName(s.first, s.second), value: Measure.toMM(l) });
          }
        }
      }
      CalcProductLogic.addInfo(c.infos, lc.t("Info_SideLength_Ground"), ground, { group: "sideLengthGround" })
      CalcProductLogic.addInfo(c.infos, lc.t("Info_SideLength_Mount"), mount, { group: "sideLengthMount" })
    }

    { // Seitenlängen Stoff
      const netGroups = SunSailLogic.calcNetPositionGroups(sunSail, {});
      const sides = topology.sides;
      topology.groups.forEach((_g, gi) => {
        const values: IProjectInfoValue = [];
        for (const s of sides) {
          if (CalcSail3dLogic.isSideVisible(sunSailType, poleCount, s)) {
            if (s.groupIndices.includes(gi)) {
              const l = Vec3.len(netGroups[gi][s.first], netGroups[gi][s.second]);
              values.push({ header: getSideNameSmall(s.first, s.second), value: Measure.toMM(l) });
            }
          }
        }
        CalcProductLogic.addInfo(c.infos, lc.t("Info_SideLength_Sail") + " " + (netGroups.length > 1 ? (gi + 1) : ""), values, { group: "sideLengthSail" + gi })
      })
    }

    { // Konkavitäten
      const values: IProjectInfoValue = [];
      const concavities = SunSailLogic.calcConcavityPercents(sunSail);
      if (concavities) {
        for (const k of getKeys(concavities)) {
          if (concavities[k] !== undefined)
            values.push({ header: k, value: concavities[k] + "%" })
        }
      }
      CalcProductLogic.addInfo(c.infos, lc.t("Sail_Concavities_Percent") + " ", values)
    }
  }

  static isSideVisible(sunSailType: SunSailType, _poleCount: number, side: ISunSailSide): boolean {
    const visible = SST.is(sunSailType, SST.CoSF) && getSideName(side.first, side.second) === "AC"
    return side.netBorder || side.axle || visible;
  }

  static doSafetyChecks(): void {

  }

  static calcCleaningSetCount(_sunSail: ISunSailData): number {
    if (SST.is(_sunSail.sunSailType, SST.Sandy))
      return 0;
    // // nicht mehr im Programm ... (21.3.2018)  -> neues Cleaning Set 11.4.2018
    // // const wallPlateCount = why calc wallplateCount?
    // for (let i = 0; i < Poles.getPoleCount(sunSail).corner; i++) {
    //   const poleType = SunSailLogic.getPoleType(sunSail, i)
    //   if (poleType === PoleType.Pole || poleType === PoleType.Wall || poleType === PoleType.Special)
    //     return 1;
    // }
    // https://plaspack.atlassian.net/browse/APP22-1010
    return 1;
  }

  static addPointArticles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId, mountPositions: IVec3[]): void {
    const lc = slc.lc;
    const sunSailType = sunSail.sunSailType;
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleId);
    const poleType = SunSailLogic.getPoleType(sunSail, poleId);

    if (SST.is(sunSailType, SST.XXs)) {
      this.addSnapForXAndXSArticles(slc, sunSail, poleId);
    }

    {
      const article = SunSailLogic.getPrimaryMountArticle(sunSail, poleId);
      if (article.articleNumber) {
        const isDouble = SunSailLogic.isDoubleMount(sunSail, poleId);
        const isTwinAxle = SST.is(sunSail.sunSailType, SST.Twin) && PoleLogic.isAxle(sunSailType, poleId);
        CalcProductLogic.addRuleArticle(slc, article.articleNumber, { affiliation: { poleId }, count: isTwinAxle ? 2 : 1, offerList: article.offerList, idCategory: "primaryMount", computed: { polePosition: poleLength - SunSailLogic.calcTopMountY(sunSail, poleId) } });
        if (isDouble)
          CalcProductLogic.addRuleArticle(slc, article.articleNumber, { affiliation: { poleId }, count: 1, offerList: "H", idCategory: "primaryMount_2", computed: { polePosition: poleLength - SunSailLogic.calcTopMountY(sunSail, poleId) - SunSailLogic.getDoubleMountOffset() } });
      }
    }

    {
      const article = SunSailLogic.getSailCoilUpArticle(sunSail, poleId);
      if (article) {
        const offerList: OfferListType = SunSailLogic.isCoilUpIncludedInSystem(sunSail.sunSailType, article) ? "N" : "H";
        const aai: AdditionalArticleInfosOption = {};
        if (article === seilStoppWinchArticleId) {
          aai.material = AdditionalArticleInfo.itemMountType({ lc }, SunSailLogic.getPoleType(sunSail, poleId), SunSailLogic.getPoleMaterial(sunSail, poleId))
        }
        CalcProductLogic.addRuleArticle(slc, article, {
          affiliation: { poleId }, count: 1, idCategory: "coilUp", offerList, additionalArticleInfo: aai
        })

        const isSnapWinch = SunSailLogic.isSnapWinch(article);
        if (isSnapWinch) {
          if (!SunSailLogic.isFallstoppIncluded(sunSail.sunSailType)) {
            CalcProductLogic.addRuleArticle(slc, "00E13000743", { affiliation: { poleId }, count: 1, idCategory: "fallstopp", offerList })
          }
        }

        const isFallStopp = SunSailLogic.isFallStopp(article);
        const isFallStoppWithoutLock = isFallStopp && article !== "00E13001631";
        if (isSnapWinch || isFallStoppWithoutLock) {
          CalcProductLogic.addRuleArticle(slc, "00E13001684", { affiliation: { poleId }, count: 0, articleType: "optional", offerList }) // Aufpreis Bügeschloss
        }

        CalcProductLogic.addRuleArticle(slc, "00E13000843", { affiliation: { poleId }, count: 0, idCategory: "coilUpSecurity", articleType: "optional", partList: { priority: PartListPriority.coilUpSecurity } });
      }

      CalcSailLogic.addSnapCounterPlateArticles(slc, sunSail, poleId)

      if (poleType === PoleType.Special) {
        CalcProductLogic.addRuleArticle(slc, "00E13001741", { affiliation: { poleId: poleId, }, count: 0, articleType: "optional", idCategory: "specialConsole", partList: { group: "Sonderkonsole" } });
        CalcProductLogic.addRuleArticle(slc, "00E13001742", { affiliation: { poleId: poleId, }, count: 0, articleType: "optional", idCategory: "specialConsole", partList: { group: "Sonderkonsole" } });
        CalcProductLogic.addRuleArticle(slc, "00E13001734", { affiliation: { poleId: poleId, }, count: 0, articleType: "optional", idCategory: "specialConsole", partList: { group: "Sonderkonsole" } });
        CalcProductLogic.addRuleArticle(slc, "00E13000216", { affiliation: { poleId: poleId, }, count: 0, articleType: "optional", idCategory: "specialConsole", partList: { group: "Sonderkonsole" } });
        CalcProductLogic.addRuleArticle(slc, "60002A000", { affiliation: { poleId: poleId, }, count: 0, articleType: "optional", idCategory: "specialConsole", partList: { group: "Sonderkonsole" } });
      }
    }

    {
      const article = SunSailLogic.getHeightChangeArticle(sunSail, poleId);
      if (article) {
        CalcProductLogic.addRuleArticle(slc, article, { affiliation: { poleId }, count: 1, idCategory: "heightChange", partList: { priority: PartListPriority.heightChange }, shoppingCartPriority: ShoppingCartPriority.PoleHeightChange })
        CalcProductLogic.addRuleArticle(slc, SunSailLogic.getHeightChangeMotorCable(sunSail, poleId), { affiliation: { poleId }, partList: { list: "stockList", priority: PartListPriority.heightChangeAdditional, group: "Motor" }, count: 1 })
      }
    }

    {
      const amplifier = AmplifierLogic.getAmplifierArticleWithSelfResponsibility(sunSail, poleId);
      if (amplifier === "__self_responsibility__") {
        //CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, getPointName(poleId), slc.lc.t("Rule_Amplifier_Self_Responsibility"))
      } else if (amplifier) {
        const poleMount = SunSailLogic.getPoleMount(sunSail, poleId);

        const amplifierInfo: IPoleAmplifierInfo | undefined = poleAmplifierArticles[amplifier];
        {
          const additionalArticleInfo: AdditionalArticleInfos = {};
          let partListName: string | undefined;
          if (amplifierInfo.type === "strengthen") {
            const length = AmplifierLogic.calcPoleAmplifierLength(sunSail, poleId);
            additionalArticleInfo.length = AdditionalArticleInfo.lengthToMM(slc, length);
            const infos: string[] = ["L = " + Measure.toMM(length)];
            if (poleMount === PoleMountType.GroundPlate && amplifierInfo.premium)
              infos.push("eingeschweißt");
            partListName = `{as400Text} (${infos.join(", ")})`;
          }

          CalcProductLogic.addRuleArticle(slc, amplifier, { affiliation: { poleId }, count: 1, idCategory: "amplifier", additionalArticleInfo, partList: { name: partListName } })
        }


        if (amplifierInfo.type === "bracing") {
          const bracing = AmplifierLogic.calcPoleBracing(sunSail, poleId);
          if (bracing)
            CalcProductLogic.addRuleArticle(slc, "VPVS" + StringUtil.pad("" + bracing.length, 3, "0"), { affiliation: { poleId }, count: bracing.count, stkUnit: "Stk", idCategory: "bracing", additionalArticleInfo: { length: AdditionalArticleInfo.lengthToMM(slc, bracing.length) } })

        }
      }
    }

    {
      const article = AmplifierLogic.getPoleSwordArticle(sunSail, poleId);
      if (article) {
        CalcProductLogic.addRuleArticle(slc, article, { affiliation: { poleId }, count: 1, idCategory: "poleSword" })
      }
    }

    switch (poleType) {
      case PoleType.Pole: {
        CalcSail3dLogic.addPoleArticles(slc, sunSail, poleId, mountPositions);
        break;
      }
      case PoleType.Wall: {
        CalcSail3dLogic.addWallArticles(slc, sunSail, poleId);
        break;
      }
      case PoleType.Special: {
        CalcSail3dLogic.addSpecialConsoleArticles(slc, sunSail, poleId);
      }
    }

    // if (SunSailLogic.getRopeFixArticle(sunSail, poleId)) { // Warnhinweis entfernen: https://plaspack.atlassian.net/browse/APP22-1098
    //   const coilUpHeigt = SunSailLogic.getRopeFixHeight(sunSail, poleId);
    //   CalcSailLogic.addError(errors, ProjectCheckErrorType.Warning, getPoleName(poleId), lc.f("Warning_CoilUp_Height", { coilUpHeight: Measure.toMM(coilUpHeigt) }))
    // }

    const mountInfo = SunSailLogic.calcPrimaryMountType(sunSail, poleId);
    switch (mountInfo.mountType) {
      case "adapter": {
        const offerList = SunSailLogic.getOfferListForAxisArticle(sunSail, poleId);
        CalcProductLogic.addRuleArticle(slc, "00E13001741", { affiliation: { poleId }, count: PoleLogic.isAxle(sunSailType, poleId) ? SunSailLogic.getAxleCount(sunSailType) : 1, offerList });
        break;
      }
      case "adapter_buegel": {
        CalcProductLogic.addRuleArticle(slc, "00E13001742", { affiliation: { poleId }, count: PoleLogic.isAxle(sunSailType, poleId) ? SunSailLogic.getAxleCount(sunSailType) : 1 });
        break;
      }
      default:
    }

    if (SunSailLogic.hasAxleThreadTensioner(sunSail, poleId)) {
      CalcProductLogic.addRuleArticle(slc, "00E13000733", { affiliation: { poleId }, count: 1, idCategory: "axle", offerList: "KH" }); // https://plaspack.atlassian.net/browse/API-94
    }

    if (!PoleLogic.isAxle(sunSail.sunSailType, poleId)) {
      const extendedMountType = SunSailLogic.calcPrimaryMountType(sunSail, poleId);
      CalcSail3dLogic.addExtendedMountTypeArticles(slc, sunSail, poleId, extendedMountType);
    } else {
      const extendedMountType = SunSailLogic.calcPrimaryMountType(sunSail, poleId);
      CalcSail3dLogic.addExtendedMountTypeArticles(slc, sunSail, poleId, extendedMountType);
    }
  }

  static addPoleArticles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId, mountPositions: IVec3[]): void {

    const pylonType = SunSailLogic.getPolePylonType(sunSail, poleId);
    const poleType = SunSailLogic.getPoleType(sunSail, poleId);
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleId);
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleId);
    const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleId);
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleId);
    const getCalculatedPoleLength = PoleChargeOptimizer.calcPoleLengthInfoFn(sunSail);
    const poleLengthB = SunSailLogic.getPoleLengthB(sunSail, poleId);
    const poleShoppingLength = getCalculatedPoleLength(poleId).shoppingLength;
    const additionalArticleInfo: AdditionalArticleInfos = {
      point: AdditionalArticleInfo.pointNameBeos(slc, poleId),
      length: AdditionalArticleInfo.lengthToMM(slc, poleLength),
      angle: AdditionalArticleInfo.angleRad(slc, poleAngle),
    }
    if (SunSailLogic.isPoleBent(sunSail, poleId))
      additionalArticleInfo.lengthB = AdditionalArticleInfo.lengthToMM(slc, poleLengthB, { headerId: "ArticleInfo_LengthB" })
    if (SunSailLogic.hasRopeFixArticle(sunSail, poleId))
      additionalArticleInfo.ropeFixHeight = AdditionalArticleInfo.lengthToMM(slc, SunSailLogic.getRopeFixHeight(sunSail, poleId), { headerId: "ArticleInfo_RopeFixHeight" })

    const articleId = getPolyOrPylonArticleId(poleMaterial, poleShoppingLength, pylonType);
    if (articleId) {
      CalcProductLogic.addRuleArticle(slc, articleId, { affiliation: { poleId }, additionalArticleInfo, shoppingCartPriority: ShoppingCartPriority.Pole });

      const materialGroup = SunSailLogic.materialGroupFromMaterial(poleMaterial, poleType);
      if (materialGroup && ["droppole", "alu", "steel", "iron48", "iron114", "iron89"].includes(materialGroup)) {
        CalcSail3dLogic.addBounceProtectionArticles(slc, poleId);
      }
      switch (materialGroup) {
        case "droppole": CalcSail3dLogic.addDroppoleArticles(slc, sunSail, poleId); break;
        case "alu": CalcSail3dLogic.addAluArticles(slc, sunSail, poleId); break;
        case "steel": CalcSail3dLogic.addSteelArticles(slc, sunSail, poleId); break;
        case "iron48": CalcSail3dLogic.addIron48Articles(slc, sunSail, poleId); break;
        case "iron114": CalcSail3dLogic.addIron114Articles(slc, sunSail, poleId); break;
        case "iron89": CalcSail3dLogic.addIron89Articles(slc, sunSail, poleId); break;
        case "telescope": CalcSail3dLogic.addTelescopeArticles(slc, sunSail, poleId); break;
        case "profile": CalcSail3dLogic.addProfileArticles(slc, sunSail, poleId); break;
      }
    }

    CalcSail3dLogic.addPylonRopeArticles(slc, sunSail, poleId, mountPositions);
    if (SunSailLogic.hasInsertDisc(poleType === PoleType.Pole, poleMaterial, poleMount)) {
      CalcProductLogic.addRuleArticle(slc, "00E13000704", { affiliation: { poleId }, count: 1, offerList: "KH" }) // Einlegescheiben
    }
  }

  static addPylonRopeArticles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId, _mountPositions: IVec3[]): void {
    // 898
    const poleType = SunSailLogic.getPoleType(sunSail, poleId);
    const pylonType = SunSailLogic.getPolePylonType(sunSail, poleId);
    const sunSailType = sunSail.sunSailType;
    if (poleType === PoleType.Pole && pylonType && !PoleLogic.isAxle(sunSailType, poleId)) {
      const { totalMaxDistIn: maxDistIn } = SunSailLogic.calcTotalMaxDistInOut(sunSail, poleId, { forPoleOnly: true })
      const pylonRopeLength = Math.ceil(maxDistIn - 7)
      CalcProductLogic.addRuleArticle(slc, "00E13000107", {
        affiliation: { poleId }, mul: pylonRopeLength, idCategory: "pylonRope", partList: { list: "none" }
      }); // https://plaspack.atlassian.net/browse/APP22-272
    }
  }

  // static addAmplifierArticleOld(slc: ICalcContext, sunSail: ISunSailData, poleIndex: number, amplifier: AmplifierTypeOld): void {
  //   let articleNumber = "";
  //   const material = SunSailLogic.getPoleMaterial(sunSail, poleIndex);
  //   const poleMount = SunSailLogic.getPoleMount(sunSail, poleIndex);
  //   if (PoleLogic.isAlu(material)) {
  //     switch (amplifier) {
  //       case "PoleAmplifier":
  //       case "PoleAmplifier_Premium": { articleNumber = "00E13000131"; break; }
  //       case "Sword_Single": { articleNumber = "00E13000202"; break; }
  //       case "Sword_Double": { articleNumber = "00E13000147"; break; }
  //     }
  //   } else if (PoleLogic.isSteel(material)) {
  //     switch (amplifier) {
  //       case "PoleAmplifier": { articleNumber = "00E13000096"; break; }
  //       case "PoleAmplifier_Premium": { articleNumber = "00E13001645"; break; }
  //       case "Sword_Single": { articleNumber = "00E13000123"; break; }
  //       case "Sword_Double": { articleNumber = "00E13000148"; break; }
  //     }
  //   }

  //   const additionalArticleInfo: Record<string, IAdditionalArticleInfoItem> = {};
  //   let partListName: string | undefined;
  //   if (amplifier === "PoleAmplifier" || amplifier === "PoleAmplifier_Premium") {
  //     const length = AmplifierLogic.calcPoleAmplifierLength(sunSail, poleIndex);
  //     additionalArticleInfo.length = AdditionalArticleInfo.lengthToMM(slc, length);
  //     const infos: string[] = ["L = " + Measure.toMM(length)];
  //     if (poleMount === PoleMountType.GroundPlate && articleNumber === "00E13001645")
  //       infos.push("eingeschweißt");
  //     partListName = `{as400Text} (${infos.join(", ")})`;
  //   }

  //   if (articleNumber)
  //     CalcSailLogic.addRuleArticle(slc, articleNumber, { keys: { poleId }, count: 1, idPart: "amplifierOld", additionalArticleInfo, partList: { name: partListName } });
  // }


  static addTelescopeArticles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): void {
    CalcSail3dLogic.addProfileArticles(slc, sunSail, poleId);
  }

  static addProfileArticles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): void {
    // const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleId);
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleId);

    switch (poleMount) {
      case PoleMountType.GroundSleeve: {
        CalcSail3dLogic.addGroundSleeveArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.EarthScrew: {
        CalcSail3dLogic.addEarthScrewArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.GroundPlate: {
        CalcSail3dLogic.addGroundPlateArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.Clamp: {
        CalcSail3dLogic.addClampMountArticles(slc, sunSail, poleId);
        break;
      }
      // case PoleMountType.ConcreteCube: {
      //   CalcSailLogic.addRuleArticle(slc, "00E13001309", { keys: { poleId }, count: 1, idPart: "poleMount" }); // BaseCube Beton L-Form mit Bodenhülse, 1150x1150x550 H=450
      //   CalcSailLogic.addRuleArticle(slc, "00E13001391", { keys: { poleId }, count: 0, articleType: "optional" }); // BaseCube Beton L-Form mit Polster und Bodenhülse, 1150x1150x550 H=450
      //   break;
      // }
      // case PoleMountType.BaseCube: {
      //   CalcSailLogic.addRuleArticle(slc, "00E13000301", { keys: { poleId }, count: 1, idPart: "poleMount" }); // BaseCube Beton L-Form mit Bodenhülse, 1150x1150x550 H=450
      //   CalcSailLogic.addRuleArticle(slc, "00E13000243", { keys: { poleId }, count: 1, idPart: "baseCube_clamp", partList: { list: "none" }, offerList: "KH", info: "Basecube" });
      //   CalcSailLogic.addRuleArticle(slc, "00E13000304", { keys: { poleId }, count: 1, articleType: "optional" }); // BaseCube Beton L-Form mit Bodenhülse, 1150x1150x550 H=450
      //   break;
      // }
    }
  }

  static addGroundPlateArticle(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): void {
    const articleId = SunSailLogic.calcGroundPlateArticleId(sunSail, poleId);
    const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleId);
    CalcProductLogic.addRuleArticle(slc, articleId, { affiliation: { poleId }, count: 1, idCategory: "poleMount", shoppingCartPriority: ShoppingCartPriority.PoleMount, additionalArticleInfo: { additionalAngleInfo: AdditionalArticleInfo.angleRad(slc, poleAngle) } });
  }

  static addGroundSleeveArticle(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): void {
    const articleId = SunSailLogic.getGroundSleeveArticle(sunSail, poleId);
    const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleId);
    CalcProductLogic.addRuleArticle(slc, articleId, { affiliation: { poleId }, count: 1, idCategory: "poleMount", shoppingCartPriority: ShoppingCartPriority.PoleMount, additionalArticleInfo: { additionalAngleInfo: AdditionalArticleInfo.angleRad(slc, poleAngle) } });
  }

  static addAluArticles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): void {
    // 2106
    const { sunSailType } = sunSail;
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleId);
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleId);
    const pylonType = SunSailLogic.getPolePylonType(sunSail, poleId);
    if (MotorLogic.hasMotor(sunSail.sunSailType)) {
      CalcProductLogic.addRuleArticle(slc, "00E13000741", { affiliation: { poleId }, count: 0, articleType: "optional" }); // Befestigungsplatte f. SO-WI-Sensor geschraubt
      CalcProductLogic.addRuleArticle(slc, "00E13001959", { affiliation: { poleId }, count: 0, articleType: "optional" }); // Adapterplatte für SoWi-Sensor inkl. Design-Mastschelle f. Aluminiummast
      CalcProductLogic.addRuleArticle(slc, "00E13001241", { affiliation: { poleId }, count: 0, articleType: "optional" }); // Befestigungsplatte f. SO-WI-Sensor mit Schelle für Alumast
    }

    const poleLengthB = SunSailLogic.getPoleLengthB(sunSail, poleId);
    if (poleLengthB > 0) {
      const amplifierArticle = AmplifierLogic.getAmplifierArticle(sunSail, poleId);
      if (amplifierArticle && poleAmplifierArticles[amplifierArticle]?.type === "strengthen")
        CalcProductLogic.addRuleArticle(slc, "00E13000160", { affiliation: { poleId }, count: 1, shoppingCartPriority: ShoppingCartPriority.PoleBend }); // Biegung Alumast mit Ver- steifungsrohr
      else
        CalcProductLogic.addRuleArticle(slc, "00E13000133", { affiliation: { poleId }, count: 1, shoppingCartPriority: ShoppingCartPriority.PoleBend }); // Biegung Aluminium-Mast ohne Versteifungsrohr
    }

    if (!pylonType) {
      const designPresetCount = SST.is(sunSailType, SST.Sandy) ? 0 : 1; // https://plaspack.atlassian.net/browse/API-62
      const aluDesignCapArticleId = (poleMaterial === "Alu_Black" ? "00E13001689" : "00E13000217")
      const ra = CalcProductLogic.addRuleArticle(slc, aluDesignCapArticleId, { affiliation: { poleId }, count: 1, articleType: "optional", shoppingCartPriority: ShoppingCartPriority.PoleCap }); // Abdeckkappe
      if (ra?.count === 0) {
        // const offerList = SST.is(sunSailType, SST.Sandy) ? "H" : "KH";
        const offerList = "KH"; // https://plaspack.atlassian.net/browse/API-62
        CalcProductLogic.addRuleArticle(slc, "60002A002", { affiliation: { poleId }, count: designPresetCount, offerList, shoppingCartPriority: ShoppingCartPriority.PoleCap }); // Abdeckkappe Design aus Aluminium eloxiert für Edelstahl-Mast
      }
    }

    // if (SST.isStandardToConcept(sunSailType)) {
    //   CalcSailLogic.addRuleArticle(slc, "00E13000219", { count: 1, poleId, articleType: "optional" }); // Mastadapter Rundschelle für Aluminium-Mast D78mm
    //   CalcSailLogic.addRuleArticle(slc, "00E13001735", { count: 1, poleId, articleType: "optional" }); // Soliday Design-Mastschelle DM 78 mm
    // }

    switch (poleMount) {
      case PoleMountType.GroundSleeve: {
        CalcSail3dLogic.addGroundSleeveArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.EarthScrew: {
        CalcSail3dLogic.addEarthScrewArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.GroundPlate: {
        CalcSail3dLogic.addGroundPlateArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.Clamp: {
        CalcSail3dLogic.addClampMountArticles(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.ConcreteCube: {
        CalcProductLogic.addRuleArticle(slc, "00E13001309", { affiliation: { poleId }, count: 1, idCategory: "poleMount", shoppingCartPriority: ShoppingCartPriority.PoleMount }); // BaseCube Beton L-Form mit Bodenhülse, 1150x1150x550 H=450
        CalcProductLogic.addRuleArticle(slc, "00E13001391", { affiliation: { poleId }, count: 0, articleType: "optional", shoppingCartPriority: ShoppingCartPriority.PoleMount }); // BaseCube Beton L-Form mit Polster und Bodenhülse, 1150x1150x550 H=450
        break;
      }
      case PoleMountType.BaseCube: {
        CalcProductLogic.addRuleArticle(slc, "00E13000301", { affiliation: { poleId }, count: 1, idCategory: "poleMount", shoppingCartPriority: ShoppingCartPriority.PoleMount }); // BaseCube Beton L-Form mit Bodenhülse, 1150x1150x550 H=450
        CalcProductLogic.addRuleArticle(slc, "00E13000243", { affiliation: { poleId }, count: 1, idCategory: "baseCube", partList: { list: "none" }, offerList: "KH", shoppingCartPriority: ShoppingCartPriority.PoleMount });
        CalcProductLogic.addRuleArticle(slc, "00E13000304", { affiliation: { poleId }, count: 1, articleType: "optional", shoppingCartPriority: ShoppingCartPriority.PoleMount }); // BaseCube Beton L-Form mit Bodenhülse, 1150x1150x550 H=450
        break;
      }
      case PoleMountType.Special: {
        CalcProductLogic.addRuleArticle(slc, "00E13001025", { affiliation: { poleId }, idCategory: "poleMount", shoppingCartPriority: ShoppingCartPriority.PoleMount }); //Sonderkonsole?
        break;
      }
    }

    CalcProductLogic.addRuleArticle(slc, "00E13001411", { affiliation: { poleId }, count: poleMount === PoleMountType.ConcreteCube ? 1 : 0, articleType: "optional", shoppingCartPriority: ShoppingCartPriority.PoleMount }); // Abdeckblende für Aluminium-Mast DM 184mm 2-teilig
    CalcProductLogic.addRuleArticle(slc, "00E13001735", { affiliation: { poleId }, count: 0, articleType: "optional", partList: { group: "Warenkorb" }, shoppingCartPriority: ShoppingCartPriority.PoleMount }); //https://plaspack.atlassian.net/browse/APP22-586
  }

  static addClampMountArticles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): void {
    // const articleId = SunSailLogic.getPoleMountClampArticle(sunSail, poleIndex);
    // if (articleId) {
    //   CalcSailLogic.addRuleArticle(slc, articleId, { count: 2, poleIndex, idPart: "poleMount" });
    // }
    const poleType = SunSailLogic.getPoleType(sunSail, poleId);
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleId);
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleId);
    const validClamps = SunSailLogic.calcPossiblePoleMountClamps(poleMount, poleMaterial, poleType);
    validClamps.articles.forEach((a, _i) => CalcProductLogic.addRuleArticle(slc, a.articleNumber, { affiliation: { poleId }, count: a.count, articleType: "optional", quickCategory: "poleClamp" }));
    validClamps.accesories.forEach((a, _i) => CalcProductLogic.addRuleArticle(slc, a.articleNumber, { affiliation: { poleId }, count: a.count, articleType: "optional", quickCategory: "poleClamp" }));
  }

  static addEarthScrewArticle(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): string {
    const articleId = SunSailLogic.getEarthScrewArticle(sunSail, poleId);
    if (articleId) {
      CalcProductLogic.addRuleArticle(slc, articleId, { affiliation: { poleId }, count: 1, idCategory: "poleMount", shoppingCartPriority: ShoppingCartPriority.PoleMount });
    }
    const validArticles = SunSailLogic.calcPossibleEarthScrews(sunSail, poleId);
    validArticles.accesories.forEach((a) => CalcProductLogic.addRuleArticle(slc, a.articleNumber, { affiliation: { poleId }, count: a.count, articleType: "optional", quickCategory: "earthScrew", shoppingCartPriority: ShoppingCartPriority.PoleMount }));
    return articleId;
  }

  static addSteelArticles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): void {
    // 2222
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleId);
    const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleId);
    const poleLengthB = SunSailLogic.getPoleLengthB(sunSail, poleId);
    const pylonType = SunSailLogic.getPolePylonType(sunSail, poleId);
    if (MotorLogic.hasMotor(sunSail.sunSailType)) {
      CalcProductLogic.addRuleArticle(slc, "00E13000741", { affiliation: { poleId }, count: 0, articleType: "optional" }); // Befestigungsplatte f. SO-WI-Sensor geschraubt
      CalcProductLogic.addRuleArticle(slc, "00E13001958", { affiliation: { poleId }, count: 0, articleType: "optional" }); // Adapterplatte für SoWi-Sensor inkl. Design-Mastschelle f. Edelstahlmast
      CalcProductLogic.addRuleArticle(slc, "00E13001242", { affiliation: { poleId }, count: 0, articleType: "optional" }); // Befestigungsplatte SO-WI-Sensor mit Schelle f.Edelstahl-Mast
    }

    if (poleLengthB > 0) {
      const angleDegree = MathUtil.rad2deg(poleAngle);
      const amplifierArticle = AmplifierLogic.getAmplifierArticle(sunSail, poleId);
      if (amplifierArticle && poleAmplifierArticles[amplifierArticle]?.type === "strengthen")
        CalcProductLogic.addRuleArticle(slc, lec(angleDegree, 20, 2) ? "00E13000739" : "00E13001634", { affiliation: { poleId }, count: 1 }); // Biegung vom Edelstahlmast mit Ver- steifungsrohr, 0°-20 vs 0-40
      else
        CalcProductLogic.addRuleArticle(slc, lec(angleDegree, 20, 2) ? "00E13000105" : "00E13001633", { affiliation: { poleId }, count: 1 }); // Biegung eines Edelstahlmastes 0°-20° vs 0-40
    }

    if (!pylonType) {
      const ra = CalcProductLogic.addRuleArticle(slc, "00E13000216", { affiliation: { poleId }, count: 1, articleType: "optional" }); // Abdeckkappe Design aus Aluminium eloxiert für Edelstahl-Mast
      if (ra?.count === 0)
        CalcProductLogic.addRuleArticle(slc, "60002A000", { affiliation: { poleId }, count: 1, offerList: "KH" }); // Abdeckkappe Design aus Aluminium eloxiert für Edelstahl-Mast
    }

    // https://plaspack.atlassian.net/browse/APP-1274
    // if (SST.isCSF(sunSailType)) {
    //  CalcSailLogic.addRuleArticle(slc, "00E13000103", { keys: { poleId }, count: 0, articleType: "optional" }); // Mastadapter Rundschelle für Edelstahl-Mast D70mm
    //  CalcSailLogic.addRuleArticle(slc, "00E13001734", { keys: { poleId }, count: 0, articleType: "optional" }); // Soliday Design-Mastschelle DM 70 mm
    // }

    switch (poleMount) {
      case PoleMountType.GroundSleeve: {
        CalcSail3dLogic.addGroundSleeveArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.EarthScrew: {
        CalcSail3dLogic.addEarthScrewArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.GroundPlate: {
        CalcSail3dLogic.addGroundPlateArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.Clamp: {
        CalcSail3dLogic.addClampMountArticles(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.ConcreteCube: {
        CalcProductLogic.addRuleArticle(slc, "00E13001309", { affiliation: { poleId }, count: 1, idCategory: "poleMount", shoppingCartPriority: ShoppingCartPriority.PoleMount }); // BaseCube Beton L-Form mit Bodenhülse, 1150x1150x550 H=450
        CalcProductLogic.addRuleArticle(slc, "00E13001391", { affiliation: { poleId }, count: 0, articleType: "optional", shoppingCartPriority: ShoppingCartPriority.PoleMount }); // BaseCube Beton L-Form mit Polster und Bodenhülse, 1150x1150x550 H=450
        break;
      }
      case PoleMountType.BaseCube: {
        CalcProductLogic.addRuleArticle(slc, "00E13000301", { affiliation: { poleId }, count: 1, idCategory: "poleMount", shoppingCartPriority: ShoppingCartPriority.PoleMount }); // BaseCube Beton L-Form mit Bodenhülse, 1150x1150x550 H=450
        CalcProductLogic.addRuleArticle(slc, "00E13000143", { affiliation: { poleId }, count: 1, idCategory: "baseCube", partList: { list: "none" }, offerList: "KH", shoppingCartPriority: ShoppingCartPriority.PoleMount });
        CalcProductLogic.addRuleArticle(slc, "00E13000304", { affiliation: { poleId }, count: 1, articleType: "optional", shoppingCartPriority: ShoppingCartPriority.PoleMount }); // BaseCube Beton L-Form mit Bodenhülse, 1150x1150x550 H=450
        break;
      }
      case PoleMountType.Special: {
        CalcProductLogic.addRuleArticle(slc, "00E13001025", { affiliation: { poleId }, idCategory: "poleMount", shoppingCartPriority: ShoppingCartPriority.PoleMount }); //Sonderkonsole?
        break;
      }
    }

    CalcProductLogic.addRuleArticle(slc, "00E13000103", { affiliation: { poleId }, count: 0, articleType: "optional", shoppingCartPriority: ShoppingCartPriority.PoleMount }); // Mastadapter Rundschelle für Edelstahl-Mast D70mm https://plaspack.atlassian.net/browse/APP-1274
    CalcProductLogic.addRuleArticle(slc, "00E13001410", { affiliation: { poleId }, count: poleMount === PoleMountType.ConcreteCube ? 1 : 0, articleType: "optional", shoppingCartPriority: ShoppingCartPriority.PoleMount }); // Abdeckblende für Aluminium-Mast DM 184mm 2-teilig
    CalcProductLogic.addRuleArticle(slc, "00E13001734", { affiliation: { poleId }, count: 0, articleType: "optional", idCategory: "additional", partList: { group: "Warenkorb" }, shoppingCartPriority: ShoppingCartPriority.PoleMount }); //https://plaspack.atlassian.net/browse/APP22-586
  }

  static addIron48Articles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): void {
    // 2367
    //const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleId);
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleId);
    switch (poleMount) {
      case PoleMountType.GroundSleeve: {
        CalcSail3dLogic.addGroundSleeveArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.EarthScrew: {
        CalcSail3dLogic.addEarthScrewArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.GroundPlate: {
        CalcSail3dLogic.addGroundPlateArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.Clamp: {
        // CalcSail3dLogic.addClampMount(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.ConcreteCube: {
        break;
      }
      case PoleMountType.BaseCube: {
        break;
      }
      case PoleMountType.Special: {
        // CalcSailLogic.addRuleArticle(slc, "00E13001025", { keys: { poleId }, idPart: "poleMount" }); //Sonderkonsole?
        break;
      }
    }

    const offerList = "H"; // https://plaspack.atlassian.net/browse/API-62 (24.10.2024 von KH auf H geändert)
    CalcProductLogic.addRuleArticle(slc, "60002A001", { affiliation: { poleId }, count: 1, offerList /*preSelected Sandy*/, shoppingCartPriority: ShoppingCartPriority.PoleCap }); // Abdeckkappe f. 48mm für Masten verz. schwarz
  }

  static addIron114Articles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): void {
    // 2367
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleId);
    switch (poleMount) {
      case PoleMountType.GroundSleeve: {
        CalcSail3dLogic.addGroundSleeveArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.EarthScrew: {
        CalcSail3dLogic.addEarthScrewArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.GroundPlate: {
        CalcSail3dLogic.addGroundPlateArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.Clamp: {
        CalcSail3dLogic.addClampMountArticles(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.ConcreteCube: {
        break;
      }
      case PoleMountType.BaseCube: {
        break;
      }
      case PoleMountType.Special: {
        CalcProductLogic.addRuleArticle(slc, "00E13001025", { affiliation: { poleId }, idCategory: "poleMount" }); //Sonderkonsole?
        break;
      }
    }

    CalcProductLogic.addRuleArticle(slc, "60002A009", { affiliation: { poleId }, count: 0, articleType: "optional" }); // Abdeckkappe f. 114 mm für Masten verzinkt
    // CalcSailLogic.addRuleArticle(slc, "00E13000833", { keys: { poleId }, count: 0, articleType: "optional" }); // Ringbolzen M10x140 für Mast verzinkt DM 114 // https://plaspack.atlassian.net/browse/APP-1259
  }

  static addIron89Articles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): void {
    // 2411
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleId);
    switch (poleMount) {
      case PoleMountType.GroundSleeve: {
        CalcSail3dLogic.addGroundSleeveArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.EarthScrew: {
        CalcSail3dLogic.addEarthScrewArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.GroundPlate: {
        CalcSail3dLogic.addGroundPlateArticle(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.Clamp: {
        CalcSail3dLogic.addClampMountArticles(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.ConcreteCube: {
        break;
      }
      case PoleMountType.BaseCube: {
        break;
      }
      case PoleMountType.Special: {
        CalcProductLogic.addRuleArticle(slc, "00E13001025", { affiliation: { poleId }, idCategory: "poleMount" }); //Sonderkonsole?
        break;
      }
    }

    CalcProductLogic.addRuleArticle(slc, "60002A010", { affiliation: { poleId }, count: 0, articleType: "optional" }); // Abdeckkappe schwarz aus Kunststoff für 89 mm Masten verz.
    // CalcSailLogic.addRuleArticle(slc, "00E13001658", { keys: { poleId }, count: 0, articleType: "optional" }); // Ringbolzen M10x110 für Mast verzinkt Ø89mm https://plaspack.atlassian.net/browse/APP-1259
  }

  static addBounceProtectionArticles(slc: IShoppingListContext, poleId: PoleId): void {
    CalcProductLogic.addRuleArticle(slc, "prallSchutz", {
      affiliation: { poleId },
      count: 0,
      articleType: "optional",
    })
  }

  static addSnapForXAndXSArticles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): void {
    // 2775
    const poleType = SunSailLogic.getPoleType(sunSail, poleId);
    if (SunSailLogic.getSailCoilUpArticle(sunSail, poleId)) {
      // CalcSailLogic.addRuleArticle(slc, "00E13001684", { keys: { poleId }, count: 0, articleType: "optional" }); // Aufpreis für Fallstopklemme mit Bügelschloss (X&XS)
      if (poleType === PoleType.Wall) {
        CalcProductLogic.addRuleArticle(slc, "00E13000777", { affiliation: { poleId }, count: 0, articleType: "optional" }); // Grundplatte Wandbefestigung aus Edelstahl für Snapsystem
      }
      CalcProductLogic.addRuleArticle(slc, "00E13001611", { affiliation: { poleId }, count: 0, articleType: "optional" }); // Winsch-Adapter mit 6-Kantaufnahme für Bohrmaschine
    }
  }

  static addDroppoleArticles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): void {
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleId);
    // const adapterCount = SST.isStandardToConcept(sunSail.sunSailType) ? 1 : 0;
    // CalcSailLogic.addRuleArticle(slc, "00E13000211", { count: adapterCount, poleId, articleType: "optional" }); // Droppole Adapter Standard für Wellenbefestigung
    // CalcSailLogic.addRuleArticle(slc, "00E13001787", { count: adapterCount, poleId, articleType: "optional" }); // Droppole Adapter Design für Wellenbefestigung

    if (MotorLogic.hasMotor(sunSail.sunSailType))
      CalcProductLogic.addRuleArticle(slc, "00E13000263", { affiliation: { poleId }, count: 0, articleType: "optional" }); // SWS-Befestigungsplatte für Droppole

    const poleMount = SunSailLogic.getPoleMount(sunSail, poleId);
    const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleId);
    switch (poleMount) {
      case PoleMountType.EarthScrew: {
        const added = CalcSail3dLogic.addEarthScrewArticle(slc, sunSail, poleId);
        if (added === "00E13000227")
          CalcProductLogic.addRuleArticle(slc, "00E13000324", { affiliation: { poleId }, count: 1, idCategory: "dorn" }); // Droppole Dorn f. Schraub- fundament 300 mm Einschub
        else if (added === "00E13000228")
          CalcProductLogic.addRuleArticle(slc, "00E13000381", { affiliation: { poleId }, count: 1, idCategory: "dorn" }); // Droppole Dorn f. Schraub- fundament 300 mm Einschub
        break;
      }
      case PoleMountType.GroundPlate: {
        // CalcSailLogic.addRuleArticle(slc, "00E13000207", { count: 0, poleId, articleType: "optional", additionalArticleInfo: [CalcSail3dLogic.getAdditionalAngleInfo(pole.angle)] }); // Droppole Bodenplatte Abdeckung RAL 9007
        // CalcSailLogic.addRuleArticle(slc, "00E13000232", { count: 0, poleId, articleType: "optional", additionalArticleInfo: [CalcSail3dLogic.getAdditionalAngleInfo(pole.angle)] }); // Droppole Bodenplatte Abdeckung schwarz
        CalcProductLogic.addRuleArticle(slc, "00E13001868", { affiliation: { poleId }, count: 0, articleType: "optional", additionalArticleInfo: { additonalAngleInfo: AdditionalArticleInfo.angleRad(slc, poleAngle) } }); // Droppole Bodenplatte Abdeckung RAL 9007
        CalcProductLogic.addRuleArticle(slc, "00E13001869", { affiliation: { poleId }, count: 0, articleType: "optional", additionalArticleInfo: { additionalAngleInfo: AdditionalArticleInfo.angleRad(slc, poleAngle) } }); // Droppole Bodenplatte Abdeckung schwarz
        CalcSail3dLogic.addGroundPlateArticle(slc, sunSail, poleId);
        CalcProductLogic.addRuleArticle(slc, "00E13001877", { affiliation: { poleId }, count: 1, idCategory: "poleMount", offerList: "KH" }); // Bodenplatte 10° Droppole
        break;
      }
      case PoleMountType.Clamp: {
        CalcSail3dLogic.addClampMountArticles(slc, sunSail, poleId);
        break;
      }
      case PoleMountType.ConcreteCube: {
        CalcProductLogic.addRuleArticle(slc, "00E13001309", { affiliation: { poleId }, count: 1, idCategory: "poleMount" }); // BaseCube Beton L-Form mit Bodenhülse, 1150x1150x550 H=450
        break;
      }
      case PoleMountType.BaseCube: {
        const angleDeg = MathUtil.rad2deg(poleAngle);
        CalcProductLogic.addRuleArticle(slc, angleDeg > 5 ? "00E13000302" : "00E13000303", { affiliation: { poleId }, count: 1, idCategory: "poleMount" }); // Base Cube Grundgestell f. Mast Droppole 10°Neigung
        CalcProductLogic.addRuleArticle(slc, "00E13000304", { affiliation: { poleId }, count: 1, articleType: "optional" }); // Base Cube Abdeckungen 5tlg. Holz "Esche"
        break;
      }
      case PoleMountType.Special: {
        CalcProductLogic.addRuleArticle(slc, "00E13001025", { affiliation: { poleId }, idCategory: "poleMount" }); //Sonderkonsole?
        break;
      }
    }

    if (SunSailLogic.isDroppoleCut(poleLength)) {
      CalcProductLogic.addRuleArticle(slc, "00E13001135", { affiliation: { poleId }, count: 1 }); // Soliday Preisaufschlag Zuschnitt auf Maß DP
    }

    CalcProductLogic.addRuleArticle(slc, "00E13001412", { affiliation: { poleId }, count: poleMount === PoleMountType.ConcreteCube ? 1 : 0, articleType: "optional" }); // Abdeckblende für Droppole-Mast 2-teilig
    CalcProductLogic.addRuleArticle(slc, "00E13001787", { affiliation: { poleId }, count: 0, articleType: "optional", idCategory: "additional", partList: { group: "Warenkorb" } }); //https://plaspack.atlassian.net/browse/APP22-586
    CalcProductLogic.addRuleArticle(slc, "00E13000206", { affiliation: { poleId }, count: 0, articleType: "optional", idCategory: "additional", partList: { group: "Warenkorb" } }); // Droppole Ring-Adapterplatte komplett
    CalcProductLogic.addRuleArticle(slc, "00E13001937", { affiliation: { poleId }, count: 0, articleType: "optional", idCategory: "additional", partList: { group: "Warenkorb" } }); // Droppole Ring-Adapterplatte kompl. mit Bohrung f. Wellenbef.
  }

  static addWallArticles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): void {
    const { sunSailType } = sunSail;
    const heightChange = SunSailLogic.getHeightChangeArticle(sunSail, poleId);
    const coilUp = SunSailLogic.hasRopeFixArticle(sunSail, poleId);

    if (heightChange) {
      if (SST.is(sunSailType, SST.AM, SST.One, SST.XXs, SST.CoSF, SST.Flex)) {
        CalcProductLogic.addRuleArticle(slc, "00E13000112", { affiliation: { poleId }, count: 1, quickCategory: "heightChange", articleType: "optional", partList: { priority: PartListPriority.heightChangeAdditional } }); // Befestigungsplatte für Gleitschiene an Wand
        // CalcSailLogic.addRuleArticle(slc, "00E13000603", { count: 1, poleIndex, quickCategory: "heightChange", articleType: "optional" }); // Soliday Gleitschiene 1,5m komplett, lose geliefert
        // if (SST.isAM(sunSailType)) {
        //   CalcSailLogic.addRuleArticle(slc, "00E13001681", { count: 0, poleIndex, quickCategory: "heightChange", articleType: "optional" }); // Gleitschiene mit Schlitten und Seilbremse lose geliefert
        // }
      }
    }

    if (!SunSailLogic.hasWallPlate1385(sunSailType, Poles.getCount(sunSail), poleId, coilUp, !!heightChange, SunSailLogic.isSlideBar(heightChange))) {
      if (SST.is(sunSailType, SST.AM, SST.SF)) { // 25.3.2024 nach Telefonat - etwas unschlüssig wo es wirklich noch hingehört
        CalcProductLogic.addRuleArticle(slc, "00E13001146", { affiliation: { poleId }, count: 0, quickCategory: "heightChange", articleType: "optional", partList: { priority: PartListPriority.heightChangeAdditional } }); // Befestigungsplatte Wand für Backenklemme
        CalcProductLogic.addRuleArticle(slc, "00E13000087", { affiliation: { poleId }, count: 0, quickCategory: "heightChange", articleType: "optional", partList: { priority: PartListPriority.heightChangeAdditional } }); //  Befestigungsplatte Wand für Autorealeaseklemme
      }
    }

    const coilUpArticle = SunSailLogic.getSailCoilUpArticle(sunSail, poleId);
    if (coilUpArticle === seilStoppWinchArticleId)
      CalcProductLogic.addRuleArticle(slc, "00E13000264", { affiliation: { poleId }, count: 0, articleType: "optional" }); //  Befestigungsplatte Winsch für MA und M Wandmontage


    CalcSail3dLogic.addWallPlates(slc, sunSail, poleId);
  }

  static addSpecialConsoleArticles(slc: IShoppingListContext, _sunSail: ISunSailData, poleId: PoleId): void {
    CalcProductLogic.addRuleArticle(slc, "00E13001025", { affiliation: { poleId }, count: 1, idCategory: "specialConsole", partList: { group: "Sonderkonsole" } });
  }

  static addWallPlates(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId): void {
    const wallPlateId = SunSailLogic.getWallPlateArticle(sunSail, poleId);
    if (wallPlateId) {
      const wallPlateCount = PoleLogic.isAxle(sunSail.sunSailType, poleId) ? AxleLogic.getAxleCountForPole(sunSail.sunSailType, poleId) : 1;

      CalcProductLogic.addRuleArticle(slc, wallPlateId, { affiliation: { poleId }, count: wallPlateCount, idCategory: "wallPlate" });
      if ((wallPlateId === "00E13000046" || wallPlateId === "00E13001862") && sunSail.sunSailType === SunSailType.SolidayCS)
        CalcProductLogic.addRuleArticle(slc, "00E13001985", { affiliation: { poleId }, count: wallPlateCount * 2, idCategory: "wallPlate", offerList: "KH" }); // https://plaspack.atlassian.net/browse/APP22-407, https://plaspack.atlassian.net/browse/APP22-584
    }

    /*
    const wallPlates = SunSailLogic.calcPossibleWallPlates(sunSail.sunSailType, Poles.getPoleCount(sunSail).corner, poleIndex, pole.coilUp?.enabled, pole.heightChange, SunSailLogic.hasSlideBar());
    const options: Partial<IShoppingArticleOptions> = {
      poleIndex,
      quickCategory: "wallmount",
    }

    for (let i = 0; i < wallPlates.length; i++) {
      const wp = wallPlates[i];
      CalcSailLogic.addRuleArticle(slc, wp, { count: i === 0 ? 1 : 0, ...options, articleType: "optional" });
    }*/
  }

  static getNotSupportedVal(): any {
    throw new Error("Not supported so far!");
  }

  static checkSideLength(errors: IProjectCheckError[], sunSailType: SunSailType, poleCount: number, netPositionGroups: IVec3[][], errorString: string, maxLength: number): void {
    const topology = SunSailTopology.calcTopology(sunSailType, poleCount);
    for (const s of topology.sides) {
      if (s.netBorder && !s.axle) {
        const netGroup = netPositionGroups[s.groupIndices[0]];
        const sideLen = Vec3.len(netGroup[s.first], netGroup[s.second]);
        if (gtc(sideLen, maxLength)) {
          const name = getSideNameSmall(s.first, s.second);
          CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, name, errorString, { params: [name, maxLength, sideLen.toFixed(2)]});
        }
      }
    }
  }

  static checkSymmetricCS(errors: IProjectCheckError[], sunSail: ISunSailData, c: PoleIndex, d: PoleIndex, lc: ILocaContext): void {
    if (SunSailLogic.hasGreenSymmetricSides(sunSail, c)) {
      const regionC = LogicS.calcAllowedRegion(sunSail, c);
      const dirC = Vec2.dir(regionC.splitter[0], regionC.splitter[1]);
      const planeC = Plane.fromPoints(
        Vec3.fromXY(regionC.splitter[0]),
        Vec3.fromXY(Vec2.add(regionC.splitter[0], dirC)),
        Vec3.addElements(Vec3.fromXY(regionC.splitter[0]), 0, 1, 0),
      )
      const distC = Plane.calcSignedDistanceToPlane(planeC, Poles.getPole(sunSail, c).position);

      const regionD = LogicS.calcAllowedRegion(sunSail, d);
      const dirD = Vec2.dir(regionD.splitter[0], regionD.splitter[1]);
      const planeD = Plane.fromPoints(
        Vec3.fromXY(regionD.splitter[0]),
        Vec3.fromXY(Vec2.add(regionD.splitter[0], dirD)),
        Vec3.addElements(Vec3.fromXY(regionD.splitter[0]), 0, 1, 0),
      )
      const distD = Plane.calcSignedDistanceToPlane(planeD, Poles.getPole(sunSail, d).position);

      if (Math.sign(distC) === Math.sign(distD)) {
        CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, getSideNameSmall(c, d), lc.t("Rule_Symmetric_CS"));
      }
    }
  }

  static checkSymmetricXS(errors: IProjectCheckError[], sunSail: ISunSailData, lc: ILocaContext): void {

    const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, undefined);
    const axlePos = SunSailLogic.getAxlePos(sunSail.sunSailType, mountPositions);
    const md = LogicS.getMountDistance(sunSail, PoleIndex.C, axlePos);
    const knotOuterDistance = SunSailLogic.getKnotOuterDist();

    const groundL6C = Vec2.fromXZ(LogicS.calcFrontNetPosition(sunSail, PoleIndex.C, knotOuterDistance + md * 0.5, axlePos));
    const groundL6D = Vec2.fromXZ(LogicS.calcFrontNetPosition(sunSail, PoleIndex.D, knotOuterDistance + md * 0.5, axlePos));

    const groundC = Vec2.fromXZ(mountPositions[PoleIndex.C]);
    const groundD = Vec2.fromXZ(mountPositions[PoleIndex.D]);
    const vecC = Vec2.dir(groundL6C, groundC);
    const vecD = Vec2.dir(groundL6D, groundD);

    const forward = Vec2.normalized(Vec2.fromXZ(LogicS.calcForwardVector(axlePos, false)));

    const vecDProj = Vec2.project(vecD, forward);
    const vecD2 = Vec2.addScaled(vecD, Vec2.to(vecD, vecDProj), 2);

    const dot = Vec2.dot(vecC, vecD2);
    const angle = Math.acos(MathUtil.clamp(dot, -1, 1));
    const threshold = MathUtil.deg2rad(10);
    const ret = Math.abs(angle) < threshold;

    if (!ret) {
      CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, getSideNameSmall(PoleIndex.C, PoleIndex.D), lc.t("Rule_Symmetric_XS"));
    }
  }

  static setSymmetricXS(sunSail: ISunSailData, p: PoleIndex): IVec3 {
    // SLogic 517
    const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, undefined);
    const axlePos = SunSailLogic.getAxlePos(sunSail.sunSailType, mountPositions);
    const md = LogicS.getMountDistance(sunSail, PoleIndex.C, axlePos);
    const knotOuterDistance = SunSailLogic.getKnotOuterDist();

    const otherPole = Poles.getOtherPole(sunSail.sunSailType, p);

    const groundL6This = Vec2.fromXZ(LogicS.calcFrontNetPosition(sunSail, p, knotOuterDistance + md * 0.5, axlePos));
    const groundL6Other = Vec2.fromXZ(LogicS.calcFrontNetPosition(sunSail, otherPole, knotOuterDistance + md * 0.5, axlePos));

    const groundOther = Vec2.fromXZ(mountPositions[otherPole]);
    const vecOther = Vec2.to(groundL6Other, groundOther);
    const forward = Vec2.normalized(Vec2.fromXZ(LogicS.calcForwardVector(axlePos, false)));

    const vecThis = Vec2.project(vecOther, forward);
    const vecThis2 = Vec2.addScaled(vecOther, Vec2.to(vecOther, vecThis), 2);

    const poleAngle = SunSailLogic.getPoleAngle(sunSail, p);
    const poleLength = SunSailLogic.getPoleLength(sunSail, p);
    const mountPosDelta = SunSailLogic.calcMountPosDelta(sunSail, p);
    const lengthBVal = SunSailLogic.getPoleLengthB(sunSail, p);
    const topMountY = SunSailLogic.calcTopMountY(sunSail, p);
    const m = PoleHelper.calcMatrixForLengthPos(sunSail.id, p, poleLength, poleAngle, lengthBVal, SunSailLogic.calcBottomPoleExtension(sunSail, p), SunSailLogic.calcPoleOffsetY(sunSail, p), poleLength - topMountY + mountPosDelta.y - 0);
    const d = Mat4.getTranslation(m);

    const vecThisNew = Vec2.addScaled(Vec2.add(groundL6This, vecThis2), vecThis2, -d.x);

    const vecThisNew3 = Vec3.fromXY(vecThisNew);
    return vecThisNew3; // position to set for other front pole
  }

  static checkMinMaxAngle(angleDeg: Degree, allowedAngles: IMinMax | number[] | number | undefined, errors: IProjectCheckError[], errorPosition: ProjectCheckErrorPosition, textExact: string, textArray: string, textMinMax: string): void {
    if (allowedAngles) {
      if (typeof allowedAngles === "number") {
        if (!eqc(angleDeg, allowedAngles, 2)) {
          CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, errorPosition, textExact, { params: [allowedAngles, angleDeg.toFixed(2)]});
        }
      } else if (Array.isArray(allowedAngles)) {
        let isOk = false;
        for (const a of allowedAngles) {
          if (eqc(angleDeg, a, 2)) {
            isOk = true;
            break;
          }
        }
        if (!isOk) {
          if (allowedAngles.length === 1) {
            // if (eqc(angleDeg, allowedAngles[0], 2))
            CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, errorPosition, textExact, { params: [allowedAngles[0], angleDeg.toFixed(2)]});
          } else {
            CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, errorPosition, textArray, { params: [allowedAngles.join(","), angleDeg.toFixed(2)]});
          }
        }
      } else {
        if (ltc(angleDeg, allowedAngles.min, 2) || gtc(angleDeg, allowedAngles.max, 2))
          CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, errorPosition, textMinMax, { params: [allowedAngles.min, allowedAngles.max, angleDeg.toFixed(2)]});
      }
    }
  }

  static addExtendedMountTypeArticles(slc: IShoppingListContext, _sunSail: ISunSailData, poleId: PoleId, mountInfo: IPointMountInfo, options?: { articleGroup?: ArticleGroup }): void {
    mountInfo.extendedMount.forEach((em, emi) => {
      if (em.articles) {
        em.articles.forEach((a, ai) => {
          CalcProductLogic.addRuleArticle(slc, a, { affiliation: { poleId }, count: 1, articleGroup: options?.articleGroup, idCategory: [options?.articleGroup, "extendedMount", "" + emi, "" + ai].filter((v) => v !== undefined).join("_") })
        })
      }
    })
  }

  static calcSecondaryArticleNumber(sunSail: ISunSailData, poleId: PoleId, mount: IPointMountInfo): string {
    let secondary: string = "";
    switch (mount.mountType) {
      case "clamp": {
        const article = SunSailLogic.getPrimaryMountArticle(sunSail, poleId); // use same as primary article...
        secondary = article.articleNumber;
        break;
      }
      case "bolt": {
        const ringSchraube = SunSailLogic.getRingschraube(SunSailLogic.getPoleMaterial(sunSail, poleId));
        if (ringSchraube)
          secondary = ringSchraube.articleNumber;
        break;
      }
    }
    return secondary;
  }

  static addSecondaryArticles(slc: IShoppingListContext, sunSail: ISunSailData, poleId: PoleId, mount: IPointMountInfo, options?: { articleGroup?: ArticleGroup; offerList?: OfferListType; computed?: IArticleComputedInfo }): void {
    const secondary = CalcSail3dLogic.calcSecondaryArticleNumber(sunSail, poleId, mount);
    if (secondary)
      CalcProductLogic.addRuleArticle(slc, secondary, { affiliation: { poleId }, count: 1, articleGroup: options?.articleGroup, idCategory: [options?.articleGroup, "mount"].filter((v) => v).join("_"), offerList: options?.offerList, computed: options?.computed });

    const additional: string[] = [];
    for (const m of mount.extendedMount) {
      if (m.articles) {
        for (const a of m.articles)
          additional.push(a);
      }
    }

    for (let i = 0; i < additional.length; i++) {
      const a = additional[i];
      CalcProductLogic.addRuleArticle(slc, a, { affiliation: { poleId }, count: 1, articleGroup: options?.articleGroup, idCategory: [options?.articleGroup, "mount", i + 1].filter((v) => v !== undefined).join("_"), offerList: options?.offerList });
    }
  }

  static addSystemAArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    const logicalAxleLength = SunSailLogic.calcAxleLength(sunSail, "logical"); // ssl.axleLength;

    if (Pvi.is(sunSail.version, "2024")) {
      // nothing
    } else {
      CalcProductLogic.addRuleArticle(slc, "00E13000847", { affiliation: { poleId: PoleIndex.A }, count: 1, articleGroup: "system", offerList: "N", partList: { priority: PartListPriority.Umlaufroller, group: "Welle" } }); // Umlaufrolle
      CalcProductLogic.addRuleArticle(slc, "00E13000848", { affiliation: { poleId: PoleIndex.C }, count: 1, articleGroup: "system", offerList: "N", partList: { priority: PartListPriority.Wirbel, group: "Welle" } }); // Wirbel
      if (gtc(logicalAxleLength, 6, 3))
        CalcProductLogic.addRuleArticle(slc, "00E13000892", { affiliation: { poleId: PoleIndex.A }, count: 1, articleGroup: "system", offerList: "N", partList: { group: "Welle" } }); // A-Verbinder
    }

    // https://plaspack.atlassian.net/browse/APP-1472
    CalcProductLogic.addRuleArticle(slc, SunSailLogic.getBackenklemmenSet(sunSail, PoleIndex.A), { affiliation: { poleId: PoleIndex.A }, count: 1, articleGroup: "system", offerList: "N", }); // Backenklemme
    CalcProductLogic.addRuleArticle(slc, "00E13000134", { affiliation: { poleId: PoleIndex.A }, count: 1, articleGroup: "system", offerList: "N" }); // Seilhalterung für Bedienseil
  }

  static addSystemOneArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    const materialGroup = SunSailLogic.getPoleMaterialGroup(sunSail, PoleIndex.A);
    const bedienSet = materialGroup ? bedienSetOne[materialGroup] : "";
    if (bedienSet) {
      CalcProductLogic.addRuleArticle(slc, bedienSet, { count: 1 }); // Umlaufrolle // "Kederkanalbolzen f. A-Anlage"
      CalcProductLogic.addRuleArticle(slc, SunSailLogic.getOneOperatingRope(sunSail), {
        partList: { list: "stockList" },
        count: 1, mul: SunSailLogic.calcOneOperatingRopeLength(sunSail), articleType: "obligatory", idCategory: "operatingRope", additionalArticleInfo: {
          usage: () => AdditionalArticleInfo.usage(slc, "RopeUsage_Operating"),
          length: (article) => AdditionalArticleInfo.lengthInM(slc, article.mul ?? 1)
        }
      }) // Seilzugsystem
    }

    const defelectionPosition = SunSailLogic.getOperatingDeflectionPosition(sunSail);
    if (defelectionPosition) {
      // https://plaspack.atlassian.net/browse/APP-1466
      CalcProductLogic.addRuleArticle(slc, "00E13001965", { affiliation: { poleId: defelectionPosition }, count: 1, offerList: "H", idCategory: "ropeDeflectionPoint" });
    }
  }

  static addSystemFlexArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    CalcProductLogic.addRuleArticle(slc, SunSailLogic.getBackenklemmenSet(sunSail, PoleIndex.A), {
      affiliation: { poleId: PoleIndex.A },
      count: 1, articleGroup: "system"
    }); // Backenklemme
  }

  static addSystemSandyArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {

    for (const poleId of Poles.getPoleIds(sunSail)) {
      if (Poles.isCenterPole(poleId)) {
        // const pid = Poles.fromPoleId(poleId);

        const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleId);
        const boltArticle = SandyLogic.getCenterRingSchraubenSetForMaterial(poleMaterial);
        const poleLength = SunSailLogic.getPoleLength(sunSail, poleId);
        CalcProductLogic.addRuleArticle(slc, boltArticle, { affiliation: { poleId }, count: 1, computed: { polePosition: poleLength - SandyLogic.calcSandyBoltOffset() } });

        if (SunSailLogic.getPoleMaterialGroup(sunSail, poleId) === "iron48") // https://plaspack.atlassian.net/browse/OA-27 Bohrung lauter Christain nur für IRON48, weil sonst Probleme mit anderen Systemen
          CalcProductLogic.addRuleArticle(slc, "99BOHRUNG001", { affiliation: { poleId }, count: 1, idCategory: "sandyOperatingPole", partList: { "list": "none" }, shoppingCartPriority: ShoppingCartPriority.PoleDrill });

        CalcProductLogic.addRuleArticle(slc, "00E13000042", { affiliation: { poleId }, count: 2, offerList: "H" }); // Professionelle Seilrolle

      } else {
        const operatingPole = SandyLogic.isRopeOperatingPole(sunSail, poleId);
        if (operatingPole) {
          const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleId);
          const boltArticles = SunSailLogic.getRingSchraubenForMaterial(poleMaterial);
          const poleLength = SunSailLogic.getPoleLength(sunSail, poleId);
          CalcProductLogic.addRuleArticle(slc, boltArticles[0], { affiliation: { poleId }, count: 1, computed: { polePosition: poleLength - SandyLogic.calcSandyBoltOffset() } });

          if (SunSailLogic.getPoleMaterialGroup(sunSail, poleId) === "iron48") // https://plaspack.atlassian.net/browse/OA-27 Bohrung lauter Christain nur für IRON48, weil sonst Probleme mit anderen Systemen
            CalcProductLogic.addRuleArticle(slc, "99BOHRUNG001", { affiliation: { poleId }, count: 1, idCategory: "sandyOperatingPole", partList: { "list": "none" }, shoppingCartPriority: ShoppingCartPriority.PoleDrill });

          if (operatingPole === "operating") {
            CalcProductLogic.addRuleArticle(slc, "00E13000042", { affiliation: { poleId }, count: 1, offerList: "H" }); // Professionelle Seilrolle
          }

          const klampeArticle = SandyLogic.getOperatingRopeKlampe(sunSail, poleId);
          if (klampeArticle) {
            CalcProductLogic.addRuleArticle(slc, klampeArticle, { affiliation: { poleId }, count: 1, idCategory: "operatingRopeKlampe", shoppingCartPriority: ShoppingCartPriority.PoleKlampe });
          }
        }
      }
    }
  }

  static addSailSandyArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {

    const cornerCount = Poles.getCount(sunSail, "corner");
    CalcProductLogic.addRuleArticle(slc, "60002A005", { count: cornerCount, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" } }); // Expanderseil - Spanngummi
    CalcProductLogic.addRuleArticle(slc, "60002A006", { count: cornerCount, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" } }); // Karabiner-Haken

    const patches = SandyLogic.getPatches(sunSail);
    for (let iPatchIndex = 0; iPatchIndex < patches.length; iPatchIndex++) {
      const patch = patches[iPatchIndex];
      if (patch?.articleId) {
        CalcProductLogic.addRuleArticle(slc, patch.articleId, { count: 1, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" } });
        const operatingRope = SandyLogic.calcPatchOperatingRope(sunSail, iPatchIndex);
        if (operatingRope) {
          CalcProductLogic.addRuleArticle(slc, operatingRope.articleId, {
            count: 1, mul: operatingRope.length, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" }, affiliation: {patchIndex: iPatchIndex}, idCategory: "patchRope", additionalArticleInfo: {
              usage: () => AdditionalArticleInfo.usage(slc, "RopeUsage_Patch"),
              length: (article) => AdditionalArticleInfo.lengthInM(slc, article.mul ?? 1)
            }
          });
        }
      }
    }

    for (let iPole = 0; iPole < Poles.getCount(sunSail, "center"); iPole++) {
      const poleId = Poles.toPoleId(iPole, "center");
      const rope = SandyLogic.calcCenterPoleOperatingRope(sunSail, poleId);
      if (rope) {
        CalcProductLogic.addRuleArticle(slc, rope.articleId, {
          count: 1, mul: rope.length, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" }, affiliation: {poleId}, idCategory: "centerSetPole", additionalArticleInfo: {
            usage: () => AdditionalArticleInfo.usage(slc, "RopeUsage_CenterPole"),
            length: (article) => AdditionalArticleInfo.lengthInM(slc, article.mul ?? 1)
          }
        });

        if (rope.articleId) {
          const kauscheArticleId = RopeHelper.getKauscheForRopeArticle(rope.articleId);
          CalcProductLogic.addRuleArticle(slc, kauscheArticleId, { count: 3, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" } });
        }
      }

      CalcProductLogic.addRuleArticle(slc, "00E13001635", { count: 1, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" }, idCategory: "centerSetSail" }); // Mittelsteher Set Segel
    }
  }

  static addSailAArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    const poleCount = Poles.getCount(sunSail);

    if (SunSailLogic.getAxleType(sunSail) === "One_65") {
      CalcProductLogic.addRuleArticle(slc, poleCount === 4 ? "00E13001929" : "00E13001930", { count: 1, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" } }); // Befestigungsset für Rollsegel 2022 für 2 Segelhälfte
    } else {
      CalcProductLogic.addRuleArticle(slc, poleCount === 4 ? "00E13001928" : "00E13001911", { count: 1, articleGroup: "sail", offerList: "N", partList: { list: "sunSailList" } }); // Umlaufrolle // "Kederkanalbolzen f. A-Anlage"
    }

    if (poleCount === 4) {
      CalcProductLogic.addRuleArticle(slc, "00E13000042", { count: 1, articleGroup: "sail", offerList: "KS", partList: { list: "sunSailList" } }); // / "Seilrolle M5x11"

      CalcProductLogic.addRuleArticle(slc, "00E13000023", {
        count: 1, mul: SunSailLogic.calcRope1Length(sunSail), articleGroup: "sail", offerList: "KS", partList: { list: "sunSailList" }, idCategory: "rope1", additionalArticleInfo: {
          usage: () => AdditionalArticleInfo.usage(slc, "RopeUsage_Rope1"),
        }
      }); // / "Seilrolle M5x11"
      CalcProductLogic.addRuleArticle(slc, RopeHelper.getKauscheForRopeArticle("00E13000023"), { count: 2, articleGroup: "sail", offerList: "KS", partList: { list: "sunSailList" }, idCategory: "rope1" });
    }

    const coilUpIndex = SunSailLogic.getSailCoilUpPoleIndex(sunSail);
    if (coilUpIndex !== -1) {
      const coilUpArticle = SunSailLogic.getSailCoilUpArticle(sunSail, coilUpIndex);
      const ropeArticleId = SunSailLogic.getRopeForCoilUpArticle(coilUpArticle);
      const kauscheId = RopeHelper.getKauscheForRopeArticle(ropeArticleId);
      CalcProductLogic.addRuleArticle(slc, ropeArticleId, {
        count: 1, mul: SunSailLogic.calcRope2Length(sunSail), articleGroup: "sail", offerList: "KS", partList: { list: "sunSailList" }, idCategory: "rope2", additionalArticleInfo: {
          usage: () => AdditionalArticleInfo.usage(slc, "RopeUsage_Rope2"),
        }
      }); // / "Seilrolle M5x11"
      CalcProductLogic.addRuleArticle(slc, kauscheId, { count: 1, articleGroup: "sail", offerList: "KS", partList: { list: "sunSailList" }, idCategory: "rope2" })
    }

    CalcProductLogic.addRuleArticle(slc, "", { count: 1, articleGroup: "system", offerList: "N", partList: { list: "sunSailList", name: getUserManualForSunSail(SunSailType.SolidayA, slc.company.language) }, idCategory: "UserManual" });
  }

  static addSailOneArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    // Seil 1: Verspannungsseil
    // Seil 2:
    if (Poles.getCount(sunSail) === 4) {

      // https://plaspack.atlassian.net/browse/APPSK-158
      CalcProductLogic.addRuleArticle(slc, "00E13001929", { count: 1, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" } }); // Befestigungsset für Rollsegel 2022 für 2 Segelhälfte
      // const schnappShackleCount = (SunSailLogic.getCornerType(sunSail, PoleIndex.B) === CornerType.BigPlate ? 1 : 0) + (SunSailLogic.getCornerType(sunSail, PoleIndex.D) === CornerType.BigPlate ? 1 : 0);

      const schnappShackleCount = (SunSailLogic.hasCornerShackle(sunSail, PoleIndex.B) ? 1 : 0) + (SunSailLogic.hasCornerShackle(sunSail, PoleIndex.D) ? 1 : 0)
      CalcProductLogic.addRuleArticle(slc, "00E13000417", { count: schnappShackleCount, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" } }); // / Schnappschäkel silber

      CalcProductLogic.addRuleArticle(slc, "00E13000107", {
        count: 1, mul: SunSailLogic.calcRope1Length(sunSail), articleGroup: "sail", offerList: "S", partList: {
          list: "sunSailList"
        }, idCategory: "rope1",
        additionalArticleInfo: {
          usage: () => AdditionalArticleInfo.usage(slc, "RopeUsage_Rope1")
        }
      }); // / "Seilrolle M5x11"
      CalcProductLogic.addRuleArticle(slc, "00E13000107", {
        count: 1, mul: SunSailLogic.calcRope2Length(sunSail), articleGroup: "sail", offerList: "S", partList: {
          list: "sunSailList"
        }, idCategory: "rope2", additionalArticleInfo: {
          usage: () => AdditionalArticleInfo.usage(slc, "RopeUsage_Rope2")
        }
      }); // / "Seilrolle M5x11"
    }
  }

  static addSailFlexArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    const axleLengthSale = SunSailLogic.calcAxleLengthSale(sunSail);
    const axleArticleBase = SunSailLogic.getAxleSystemBaseArticleId(sunSail.sunSailType, Poles.getCount(sunSail), sunSail.version, SunSailLogic.calcPylonCount(sunSail), sunSail.axleColor);
    CalcProductLogic.addRuleArticle(slc, axleArticleBase + StringUtil.pad("" + axleLengthSale, 2, "0"), { count: 1, idCategory: "axle", offerList: "S", partList: { list: "sunSailList" } })
  }

  static addSystemMArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    if (Pvi.eq(sunSail.version, "2024")) {
      CalcProductLogic.addRuleArticle(slc, "00E13002106", { count: 1, articleType: "optional" }); // https://plaspack.atlassian.net/browse/API-93
    } else {
      CalcProductLogic.addRuleArticle(slc, "00E13000845", { affiliation: { poleId: PoleIndex.A }, count: 1, articleGroup: "system", offerList: "N", partList: { priority: PartListPriority.Umlaufroller, group: "Welle" } }); // Umlaufrolle
      CalcProductLogic.addRuleArticle(slc, "00E13000846", { affiliation: { poleId: PoleIndex.C }, count: 1, articleGroup: "system", offerList: "N", partList: { priority: PartListPriority.Wirbel, group: "Welle" } }); // Wirbel
      CalcProductLogic.addRuleArticle(slc, "00E13000432", { affiliation: { poleId: PoleIndex.A }, count: 1, articleGroup: "system", offerList: "N", partList: { group: "Welle" } }); // "Augterminal verschraubbar (geschliffen)"
    }

    // https://plaspack.atlassian.net/browse/APP-1472
    CalcProductLogic.addRuleArticle(slc, SunSailLogic.getBackenklemmenSet(sunSail, PoleIndex.A), { affiliation: { poleId: PoleIndex.A }, count: 1, articleGroup: "system", offerList: "N", }); // Backenklemme
    CalcProductLogic.addRuleArticle(slc, "00E13000134", { affiliation: { poleId: PoleIndex.A }, count: 1, articleGroup: "system", offerList: "N" }); // Seilhalterung für Bedienseil
  }

  static addSailMArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    const poleCount = Poles.getCount(sunSail);
    // CalcSailLogic.addRuleArticle(slc, "00E13001506", { count: poleCount === 4 ? 4 : 2, articleGroup: "sail", offerList: "N", partList: "sunSailList" }); // Umlaufrolle // "Kederkanalbolzen f. A-Anlage"

    if (SunSailLogic.getAxleType(sunSail) === "M_30") {
      CalcProductLogic.addRuleArticle(slc, poleCount === 4 ? "00E13001929" : "00E13001930", { count: 1, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" } }); // Befestigungsset für Rollsegel 2022 für 2 Segelhälfte
    } else {
      CalcProductLogic.addRuleArticle(slc, "00E13000413", { count: 2, articleGroup: "sail", offerList: "N", partList: { list: "sunSailList" } }); // Schäkel 2 cm zum Verschrauben für die Befestigung des Segels an der Welle // https://plaspack.atlassian.net/jira/software/projects/APPSK/boards/5?selectedIssue=APPSK-54
    }

    if (poleCount === 4) {
      CalcProductLogic.addRuleArticle(slc, "00E13000042", { count: 1, articleGroup: "sail", offerList: "KS", partList: { list: "sunSailList" } }); // / "Seilrolle M5x11"
      CalcProductLogic.addRuleArticle(slc, "00E13000023", {
        count: 1, mul: SunSailLogic.calcRope1Length(sunSail), articleGroup: "sail", offerList: "KS", partList: { list: "sunSailList" }, idCategory: "rope1", additionalArticleInfo: {
          usage: () => AdditionalArticleInfo.usage(slc, "RopeUsage_Rope1"),
        }
      }); // / "Seilrolle M5x11"
      CalcProductLogic.addRuleArticle(slc, RopeHelper.getKauscheForRopeArticle("00E13000023"), { count: 2, articleGroup: "sail", offerList: "KS", partList: { list: "sunSailList" }, idCategory: "rope1" });
    }

    const coilUpIndex = SunSailLogic.getSailCoilUpPoleIndex(sunSail);
    if (coilUpIndex !== -1) {
      const coilUpArticle = SunSailLogic.getSailCoilUpArticle(sunSail, coilUpIndex);
      const ropeArticleId = SunSailLogic.getRopeForCoilUpArticle(coilUpArticle);
      const kauscheId = RopeHelper.getKauscheForRopeArticle(ropeArticleId);
      CalcProductLogic.addRuleArticle(slc, ropeArticleId, {
        count: 1, mul: SunSailLogic.calcRope2Length(sunSail), articleGroup: "sail", offerList: "KS", partList: { list: "sunSailList" }, idCategory: "rope2", additionalArticleInfo: {
          usage: () => AdditionalArticleInfo.usage(slc, "RopeUsage_Rope2"),
        }
      }); // / "Seilrolle M5x11"
      CalcProductLogic.addRuleArticle(slc, kauscheId, { count: 1, articleGroup: "sail", offerList: "KS", partList: { list: "sunSailList" }, idCategory: "rope2" })
    }

    CalcProductLogic.addRuleArticle(slc, "", { count: 1, articleGroup: "system", offerList: "N", partList: { list: "sunSailList", name: getUserManualForSunSail(SunSailType.SolidayM, slc.company.language) }, idCategory: "UserManual" });
  }

  static addSystemCArticles(_slc: IShoppingListContext, _sunSail: ISunSailData): void {
  }

  static addSailRope1Articles(slc: IShoppingListContext, length: number): void {
    if (length > 0) {
      CalcProductLogic.addRuleArticle(slc, "00E13000023", {
        count: 1, mul: length, articleGroup: "sail", offerList: "S", idCategory: "rope1", partList: { list: "sunSailList" }, additionalArticleInfo: {
          usage: () => AdditionalArticleInfo.usage(slc, "RopeUsage_Rope1"),
        }
      }) // role // offerList: "S"
      CalcProductLogic.addRuleArticle(slc, "00E13000053", { count: 2, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" }, idCategory: "rope1" }) // K // , offerList: "S" // Kausche => handleSailC
      CalcProductLogic.addRuleArticle(slc, "00E13000417", { count: 1, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" }, idCategory: "rope1" }); // / Schnappschäkel silber
      CalcProductLogic.addRuleArticle(slc, "00E13001151", { count: 1, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" }, idCategory: "rope1" }) // role // , partList: "sunSailList"
    }
  }

  static addSailCArticles(slc: IShoppingListContext, ssd: ISunSailData): void {
    CalcSail3dLogic.addSailRope1Articles(slc, SunSailLogic.calcRope1Length(ssd));

    if (SunSailLogic.getAxleType(ssd) === "C_90") {
      CalcProductLogic.addRuleArticle(slc, "00E13001929", { count: 1, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" } }); // Befestigungsset für Rollsegel 2022 für 2 Segelhälfte
      // CalcSailLogic.addRuleArticle(slc, "00E13000656", { count: 1, articleGroup: "system", offerList: "KS", partList: "sunSailList" }); // / Befestigungsset C-Segel f Doppelkedernut kompl. (Huterl) // https://plaspack.atlassian.net/browse/APP22-831
      CalcProductLogic.addRuleArticle(slc, "00E13000417", { count: 2, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" } }); // / Schnappschäkel silber

    } else {
      CalcProductLogic.addRuleArticle(slc, "00E13001885", { count: 1, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" } }); // Befestigungsset für Rollsegel 2022 für 2 Segelhälfte // https://plaspack.atlassian.net/browse/APPSK-157
      CalcProductLogic.addRuleArticle(slc, "00E13000656", { count: 1, articleGroup: "system", offerList: "KS", partList: { list: "sunSailList" } }); // Befestigungsset C-Segel f Doppelkedernut kompl. (Huterl)
      CalcProductLogic.addRuleArticle(slc, "00E13000417", { count: 2, articleGroup: "sail", offerList: "S", partList: { list: "sunSailList" } }); // / Schnappschäkel silber
    }

    CalcProductLogic.addRuleArticle(slc, "", { count: 1, articleGroup: "system", offerList: "N", partList: { list: "sunSailList", name: getUserManualForSunSail(SunSailType.SolidayC, slc.company.language) }, idCategory: "UserManual" });
  }

  static addSystemXArticles(_slc: IShoppingListContext, sunSail: ISunSailData): void {
    // Wenn die Fallstopklemme (= Bedienung) auf einem Droppole-Mast ist, dann soll im Exportfile der -- A. Pohn 24.2.2021 (X immer mit Winch und Fallstopklemme)
    const coilUpPole = SunSailLogic.getSailCoilUpPoleIndex(sunSail);
    if (coilUpPole !== -1) {
      // const coilUpArticle = SunSailLogic.getRopeFixArticle(sunSail, coilUpPole);
      // if (SunSailLogic.isSnapWinch(coilUpArticle) || SunSailLogic.isFallStopp(coilUpArticle)) {
      //   if (PoleLogic.isDroppole(SunSailLogic.getPoleMaterial(sunSail, coilUpPole)))
      //     CalcSailLogic.addRuleArticle(slc, "00E13000809", { count: 1, articleGroup: "system", offerList: "KH" }); // / Gegenplatte zu Snap-Grundplatte bei Droppole-Mast

      // CalcSailLogic.addSnapCounterPlate(slc, sunSail, coilUpPole);
    }
  }

  static addSailXArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {

    if (Poles.getCount(sunSail) !== 4)
      return;

    CalcProductLogic.addRuleArticle(slc, "00E13000023", {
      count: 1, mul: SunSailLogic.calcRope1Length(sunSail), articleGroup: "sail", offerList: "KS", partList: { list: "sunSailList" }, idCategory: "rope1", additionalArticleInfo: {
        usage: () => AdditionalArticleInfo.usage(slc, "RopeUsage_Rope1"),
      }
    }); // / "Seilrolle M5x11"
    CalcProductLogic.addRuleArticle(slc, RopeHelper.getKauscheForRopeArticle("00E13000023"), { count: 2, articleGroup: "sail", offerList: "KS", partList: { list: "sunSailList" }, idCategory: "rope1" });

    CalcProductLogic.addRuleArticle(slc, "00E13001310", { count: 1, articleGroup: "system", offerList: "KS", partList: { list: "sunSailList" } }); // / Doppelseilrolle
    CalcProductLogic.addRuleArticle(slc, "00E13001885", { count: 1, articleGroup: "system", offerList: "KS", partList: { list: "sunSailList" } }); // / Befestigungsset für Rollsegel 2022 für 1 Segelhälfte
    CalcProductLogic.addRuleArticle(slc, "00E13000615", { count: 1, articleGroup: "system", offerList: "KS", partList: { list: "sunSailList" } }); // / "Ringschraube M5x11"

    // CalcSailLogic.addRuleArticle(slc, "00E13000658", { count: 1, articleGroup: "system", offerList: "KS", partList: { list: "sunSailList" } }); // / Einzelkedernut
    // CalcSailLogic.addRuleArticle(slc, "00E13000615", { count: 1, articleGroup: "system", offerList: "KS", partList: { list: "sunSailList" } }); // / "Ringschraube M5x11"
    // CalcSailLogic.addRuleArticle(slc, ArticleInfo.getKauscheIdForDiameter(6.5), { count: 2, articleGroup: "system", offerList: "KS", partList: { list: "sunSailList" } }); // / "Ringschraube M5x11"

    // * rope2 => QuickCombo article => sunSail.articleSlots.rope2
    // * Rolle abhängig von Gleitschiene-Artikel oder von Bedienung...
    CalcSailLogic.addRope2Articles(slc, sunSail);
  }

  static addSystemCSArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    if (Poles.getCount(sunSail) === 4) {
      CalcProductLogic.addRuleArticle(slc, "00E13000438", { count: 1, articleGroup: "system", offerList: "KH" }); // Verspannungsset CS/XS Dreiecksabspannung
    }
  }

  static addSailCSArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    const poleCount = Poles.getCount(sunSail);
    const schnappSchaekelCount = poleCount - 2;
    CalcProductLogic.addRuleArticle(slc, "00E13000417", { count: schnappSchaekelCount, articleGroup: "system", offerList: "KS", partList: { list: "sunSailList" } }); // Schnappschäkel silber

    if (SunSailLogic.getAxleType(sunSail) === "CS_110_02") {
      CalcProductLogic.addRuleArticle(slc, "00E13001929", { count: 1, articleGroup: "system", offerList: "KS", partList: { list: "sunSailList" } }); // Befestigungsset für Rollsegel 2022
      // if (poleCount === 4)  https://plaspack.atlassian.net/browse/APP22-342
      {
        // CalcSailLogic.addRuleArticle(slc, "00E13000577", { count: 2, articleGroup: "sail", offerList: "KS", partList: "sunSailList" }); // Schrauben/Muttern => removed because of One Drive
        // CalcSailLogic.addRuleArticle(slc, "00E13000549", { count: 2, articleGroup: "sail", offerList: "KS", partList: "sunSailList" }); // Schrauben/Muttern
      }
    } else if (SunSailLogic.getAxleType(sunSail) === "C_84") {
      CalcProductLogic.addRuleArticle(slc, "00E13000656", { count: 1, articleGroup: "system", offerList: "KS", partList: { list: "sunSailList" } }); // Befestigungsset C-Segel f Doppelkedernut kompl. (Huterl) // https://plaspack.atlassian.net/browse/APPSK-169
    } else {
      const count = 2; // ?? poleCount === 4 ? 2 : 1;
      CalcProductLogic.addRuleArticle(slc, "00E13000818", { count, articleGroup: "system", offerList: "KS", partList: { list: "sunSailList" } }); // Befestigungsset für Rollsegel 2022
      // CalcSailLogic.addRuleArticle(slc, "00E13000577", { count, articleGroup: "sail", offerList: "KS", partList: "sunSailList" }); // Schrauben/Muttern removed because of One Drive
      // CalcSailLogic.addRuleArticle(slc, "00E13000549", { count, articleGroup: "sail", offerList: "KS", partList: "sunSailList" }); // Schrauben/Muttern
      if (poleCount === 4) { // One Drive - Telefonat 15.3.2022
        CalcProductLogic.addRuleArticle(slc, "00E13000577", { count: 2, articleGroup: "sail", offerList: "KS", partList: { list: "sunSailList" } }); // Schrauben/Muttern => removed because of One Drive
        CalcProductLogic.addRuleArticle(slc, "00E13000549", { count: 2, articleGroup: "sail", offerList: "KS", partList: { list: "sunSailList" } }); // Schrauben/Muttern
      }
    }

    CalcProductLogic.addRuleArticle(slc, "", { count: 1, articleGroup: "system", offerList: "N", partList: { list: "sunSailList", name: getUserManualForSunSail(SunSailType.SolidayCS, slc.company.language) }, idCategory: "UserManual" });
  }

  static addSystemTwinArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    const poleCount = Poles.getCount(sunSail);
    if (poleCount >= 4) {
      const count = poleCount === 6 ? 2 : 1;
      CalcProductLogic.addRuleArticle(slc, "00E13000438", { count, articleGroup: "system", offerList: "KH" }); // Verspannungsset CS/XS Dreiecksabspannung
    }
  }

  static addSailTwinArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    const poleCount = Poles.getCount(sunSail);
    const schnappSchaekelCount = poleCount - 2;
    CalcProductLogic.addRuleArticle(slc, "00E13000417", { count: schnappSchaekelCount, articleGroup: "system", offerList: "KS", partList: { list: "sunSailList" } }); // Schnappschäkel silber

    if (SunSailLogic.getAxleType(sunSail) === "CS_110_02") {
      CalcProductLogic.addRuleArticle(slc, "00E13001929", { count: 2, articleGroup: "system", offerList: "KS", partList: { list: "sunSailList" } }); // Befestigungsset für Rollsegel 2022
      // if (poleCount >= 4) { // OneDrive Änderung
      //   const count = 4; // poleCount === 6 ? 4 : 2;
      //   CalcSailLogic.addRuleArticle(slc, "00E13000577", { count, articleGroup: "sail", offerList: "KS", partList: "sunSailList" }); // Schrauben/Muttern
      //   CalcSailLogic.addRuleArticle(slc, "00E13000549", { count, articleGroup: "sail", offerList: "KS", partList: "sunSailList" }); // Schrauben/Muttern
      // }
    } else if (SunSailLogic.getAxleType(sunSail) === "C_84") {
      CalcProductLogic.addRuleArticle(slc, "00E13000656", { count: 2, articleGroup: "system", offerList: "KS", partList: { list: "sunSailList" } }); // Befestigungsset C-Segel f Doppelkedernut kompl. (Huterl) // https://plaspack.atlassian.net/browse/APPSK-169
    } else {
      CalcProductLogic.addRuleArticle(slc, "00E13000818", { count: 2 * 2, articleGroup: "system", offerList: "KS", partList: { list: "sunSailList" } }); // Befestigungsset für Rollsegel 2022
      // CalcSailLogic.addRuleArticle(slc, "00E13000577", { count, articleGroup: "sail", offerList: "KS", partList: "sunSailList" }); // Schrauben/Muttern
      // CalcSailLogic.addRuleArticle(slc, "00E13000549", { count, articleGroup: "sail", offerList: "KS", partList: "sunSailList" }); // Schrauben/Muttern
      if (poleCount >= 4) { // OneDrive Änderung
        const count = poleCount === 5 ? 2 : 4; // poleCount === 6 ? 4 : 2; // onedrive (https://plaspack.atlassian.net/browse/APP22-342)
        CalcProductLogic.addRuleArticle(slc, "00E13000577", { count, articleGroup: "sail", offerList: "KS", partList: { list: "sunSailList" } }); // Schrauben/Muttern
        CalcProductLogic.addRuleArticle(slc, "00E13000549", { count, articleGroup: "sail", offerList: "KS", partList: { list: "sunSailList" } }); // Schrauben/Muttern
      }
    }

    CalcProductLogic.addRuleArticle(slc, "", { count: 1, articleGroup: "system", offerList: "N", partList: { list: "sunSailList", name: getUserManualForSunSail(SunSailType.SolidayTwin, slc.company.language) }, idCategory: "UserManual" });
  }

  static addSystemXSArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    const sunSailType = sunSail.sunSailType;
    const poleCount = Poles.getCount(sunSail);
    if (sunSailType === SunSailType.SolidayXS) {
      if (poleCount === 4) {
        CalcProductLogic.addRuleArticle(slc, "00E13001504", { count: 1, articleGroup: "system", offerList: "KH" }); // Wellenbefestigungsset komplett
        CalcProductLogic.addRuleArticle(slc, "00E13000438", { count: 1, articleGroup: "system", offerList: "KH" }); // Verspannungsset CS/XS Dreiecksabspannung
        CalcProductLogic.addRuleArticle(slc, "00E13000042", { count: 2, articleGroup: "system", offerList: "H" }); // Seilrolle
      } else if (poleCount === 3) {
        // CalcSailLogic.addRuleArticle(slc, "00E13000026", { count: 2, articleGroup: "system", offerList: "H" }); // Seilrolle
        // CalcSailLogic.addRuleArticle(slc, "00E13000042", { count: 2, articleGroup: "system", offerList: "H" }); // Seilrolle
      }
    }
  }

  static addSailXSArticles(slc: IShoppingListContext, sunSail: ISunSailData): void {
    // * rope2 => QuickCombo article => sunSail.articleSlots.rope2
    // * Rolle abhängig von Gleitschiene-Artikel oder von Bedienung...
    CalcSailLogic.addRope2Articles(slc, sunSail);
    // CalcSailLogic.addRuleArticle(slc, "00E13000042", { count: 2, partList: "sunSailList", offerList: "S", idPart: "sail_roles" }) // Seilrollen / bei Hardware oder Segel 03.02.2022 => bei Hardware

    if (Poles.getCount(sunSail) === 3) {
      CalcProductLogic.addRuleArticle(slc, "00E13001885", { count: 1, partList: { list: "sunSailList" }, offerList: "S" }) // Befestigungsset für XS
      CalcProductLogic.addRuleArticle(slc, "00E13000026", { count: 1, articleGroup: "system", offerList: "S" }); // Schäkel
      CalcProductLogic.addRuleArticle(slc, "00E13000042", { count: 1, articleGroup: "system", offerList: "S" }); // Seilrolle
    } else {
      CalcProductLogic.addRuleArticle(slc, "00E13001430", { count: 1, partList: { list: "sunSailList" }, offerList: "S" }) // Befestigungsset für XS
    }
  }

  static addSunSailBagArticles(slc: IShoppingListContext, sunSailAreas: number[], initialCount: number): void {
    const articles: string[] = sunSailAreas.map((sunSailArea) => {
      if (lec(sunSailArea, 20, 2))
        return "00E13000827";
      else if (lec(sunSailArea, 45, 2))
        return "00E13000828"
      else if (lec(sunSailArea, 65, 2))
        return "00E13000829"
      return "";
    })
    const withoutDuplicates = articles.filter((article, index) => article && articles.indexOf(article) === index).sort((a, b) => a.localeCompare(b))
    withoutDuplicates.forEach((article) => {
      const options: Partial<IShoppingArticleOptions> = { count: initialCount, offerList: "S", partList: { list: "sunSailList" }, articleType: "optional", idCategory: "sunSailBag" }
      CalcProductLogic.addRuleArticle(slc, article, options);
    });
  }

  static addRevolvingRopeArticles(slc: IShoppingListContext, sunSail: ISunSailData, systemArticle: IRuleArticle): void {
    const revolvingRope = SunSailLogic.getRevolvingRope(sunSail);
    if (revolvingRope) {
      // do we need both?
      CalcProductLogic.addRuleArticle(slc, revolvingRope, {
        parentArticle: systemArticle,
        count: 1,
        offerList: "KH",
        idCategory: "revolvingRope",
        partList: { list: "none" }
      });
      CalcProductLogic.addRuleArticle(slc, revolvingRope, {
        count: 1,
        offerList: "N",
        idCategory: "revolvingRope",
        partList: {
          list: "stockList",
          priority: PartListPriority.RevolvingRope,
          group: SunSailLogic.getSystemPackingArticle(sunSail.sunSailType, sunSail.version) ? SYSTEM_PACKAGING : "Welle"
        }
      });
    }
  }
}
